/**
 * HtmlViewer — renders HTML content in a sandboxed iframe.
 *
 * Two modes:
 *   1. **Session mode** (baseUrl set): iframe `src` points to the preview subdomain.
 *      Content is origin-isolated — scripts cannot access main app cookies/API.
 *      Updates via SSE reload with double-buffered iframes (no flash).
 *
 *   2. **Inline mode** (baseUrl null): iframe `srcDoc` with injected content.
 *      Used for version preview, standalone HTML, or when VITE_PREVIEW_ORIGIN
 *      is not configured. Sandbox uses a unique opaque origin (no
 *      `allow-same-origin`) so scripts cannot reach the host app's cookies,
 *      storage, or API. Cross-file resource loading via `<base href>` is NOT
 *      supported in this mode — use session mode for multi-file content.
 *
 * Sandbox shape: `allow-scripts allow-popups allow-forms`.
 *   - `allow-forms` (#1002) — without it the browser drops a submission BEFORE
 *     firing `submit`, so an app's `onSubmit` + `preventDefault()` never runs
 *     (silently: the block message reaches no debug log). Leaving it out kept no
 *     data in. A form adds an un-CORSed cross-origin POST body outside
 *     `connect-src` — but the frame already has one: it can load an attacker's
 *     page as a nested frame (`frame-src https:`) or a popup, which runs
 *     scripts (sandbox flags inherit) under ITS server's CSP, and `postMessage`
 *     it any data to send anywhere. And an app's backend reaches the network
 *     anyway. A form POST to a third party is what any website can send.
 *     A form POST from the frame carries `Origin: null`, which
 *     `requireSameOrigin` refuses on every cookie-authed API prefix — exactly as
 *     for a form on any website. The served `/s/` CSP already granted it, and pd.app sets no sandbox. No
 *     `form-action` either, for the same reason; the debug-log shim warns about
 *     a submission no handler prevented instead (preview-console.ts).
 * Notably absent:
 *   - `allow-same-origin` — opaque origin keeps the iframe isolated.
 *   - `allow-top-navigation` — iframe can't redirect the embedder.
 *   - `allow-popups-to-escape-sandbox` — popups *inherit* the sandbox, so
 *     `window.open(attacker)` loads attacker.com in an OPAQUE origin: its
 *     scripts still run (`allow-scripts` inherits), but without attacker.com's
 *     own cookies or storage. Trade-off: legitimate `<a target="_blank">` opens
 *     in a sandboxed window — readable, but interactive features that
 *     depend on origin policy may be degraded.
 *
 * Features (both modes):
 *   - The same white backdrop in every mode (see PREVIEW_BACKDROP)
 *   - ViewSync scroll via postMessage (works cross-origin)
 *   - Reports navigation events to parent via onNavigate callback
 *   - `papan:openExternal` messages delegate to `onRequestOpenExternal`
 *     (parent-side confirm flow) when provided, otherwise fall back to a
 *     direct `window.open`.
 */

import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react'
// Pure, zero-import string builder (client-safe subpath — does NOT pull the
// server `@papan/files/api` barrel). Shared with the server-injected session
// preview so the snapshot serialization is identical in both modes.
import { getSnapshotScript } from '@papan/files/api/snapshot-script'
import { getConsoleCaptureScript } from '@papan/files/api/console-script'
import { getSandboxStorageShimScript } from '@papan/files/api/storage-shim-script'
import { ensureDefaultAppLink } from '@papan/share-links/client'
import { APP_ORIGIN_FRAME_SANDBOX, APP_ORIGIN_FRAME_ALLOW, OPAQUE_FRAME_SANDBOX, frameAppOriginFor } from './app-origin-frame.js'
import { injectAtHeadStart } from '@papan/files/api/head-inject'
import {
  acceptIframeRefs,
  addScopedMessageListener,
  useGatedMessageDispatcher,
  useWarnOnce,
} from './iframe-message-gate.js'
import { SdkConsentDialog, type SdkConsentRequest } from './SdkConsentDialog.js'
import { fetchConsentRequirement } from '../client/consent-requirement.js'
import { useHandoffRelay } from './useHandoffRelay.js'
import { PdappCapsule } from './PdappCapsule.js'
import { useHandoffDragRelay } from './useHandoffDragRelay.js'
import { PAPAN_FRAME_ATTR } from './papan-input.js'
import { emitAppRunNotice } from './app-run-notice.js'
import { SHARE_SIGN_IN_EVENT, shareSessionContext } from './share-session-context.js'
import { labelFor } from '@papan/sdk/consent'
import { transportErrorBody, dbTimeoutMs, dbTimedOutBody, dbArgsNotSendableBody } from '@papan/sdk/transport-error'
// #471 — the SAME poll the npm SDK uses for external pages. Two copies of "has
// this finished yet?" is how a page and its host end up disagreeing about
// whether a run is still going; same reason `transport-error` is shared above.
import { ASYNC_RUNS_HEADER, ASYNC_RUNS_VALUE, isQueuedRun, isTerminalPollStatus, mintRunId, pollRun, type RunStatusBody } from '@papan/sdk/run-poll'
import { getDefaultHeaders, getActiveOrgEmail, getActiveTenantId, getApiBase, isSessionEndedError, sessionFetch, currentSessionSignal, sessionEndedSince, SessionEndedError, readInSession } from '@papan/api-client'
import { useRealtimeOptional } from '@papan/realtime/client'
import { useBrand } from '@papan/brand/react'
import { useThemeOptional, getThemeClass } from '@papan/core-ui/ui'
import { encodeServePath } from '../client/serveToken.js'
import { answerPageLink, type AppPageRef } from './app-page-link-answer.js'
import { appOwnUrlSuffix } from '@papan/types/app-page-link'

export interface HtmlViewerHandle {
  iframe: HTMLIFrameElement | null
  /** Soft-reload the preview iframe — the SAME double-buffered swap the SSE
   *  auto-reload uses (no remount, no session re-mint, no flash). Drives the
   *  editor's manual Refresh button. */
  reload: () => void
  /** Request a DOM snapshot from the preview iframe (session mode only).
   *  Returns script-free HTML with all assets inlined as data URIs. */
  requestSnapshot: () => Promise<string | null>
  /** Post a design-mode command to the active preview iframe (session mode).
   *  Used by the editor's breadcrumb bar: `papan:design-select-loc` /
   *  `papan:design-clear`. No-op when no iframe is mounted. */
  postDesignCommand: (msg: { type: string; [k: string]: unknown }) => void
  /** Declare that the displayed iframe ALREADY reflects `content` — used by
   *  design-mode write-backs, which mutate the source AND optimistically
   *  patch the live DOM (papan:design-style / contenteditable). Marking the
   *  content as rendered suppresses BOTH reload paths for this exact
   *  content (the local-typing debounce and the SSE skip-if-unchanged
   *  check) so a panel tweak doesn't tear down iframe state. Any FURTHER
   *  change — another user, an agent edit, typing in the Source pane, a
   *  sibling asset — produces different content and reloads as before.
   *  Also books the change into the iframe's patch-engine baseline
   *  (`applied: true` sync) so subsequent typing diffs stay single-region. */
  markContentRendered: (content: string) => void
  /** Force any pending content sync (the 1.5s typing debounce) through the
   *  patch engine NOW and resolve when the preview state is settled:
   *  `true` = the iframe reflects the current content (patched or already
   *  in sync — offsets/journals are coherent, safe to commit a design
   *  edit); `false` = a reload was triggered (anchors are about to be
   *  re-derived; the caller should drop its pending edit). Design commits
   *  call this first — a remote collaborator edit can land in the Y.Doc
   *  well before the debounce relays it to the journals, and committing
   *  inside that window mis-anchors. */
  flushPendingPatch: () => Promise<boolean>
  /** The content change about to arrive is a SINGLE discrete action (an undo
   *  or redo), not a stream. A patch refusal normally waits to coalesce with
   *  whatever is still being typed; there is nothing following this one, so
   *  that wait is pure latency in front of a reload the user is watching for.
   *  Reload immediately instead. One-shot: consumed by the next attempt,
   *  whether it patches or reloads. */
  expediteNextReload: () => void
}

/** Element selection reported by the injected design-mode inspector
 *  (design-mode-script.ts). `loc` anchors the element in the ORIGINAL
 *  source of the served file (1-based line:col from the serve-time
 *  parse5 tagging), `file` is the served path relative to the preview
 *  root (multi-page apps), `breadcrumb` is outermost-first ancestors. */
export interface DesignElementSelection {
  loc: { startLine: number; startCol: number; endLine: number; endCol: number }
  /** Raw `data-papan-loc` attribute value — segments 5/6 are the start
   *  tag's source offsets (style write-back), segment 7 the inner end. */
  locAttr: string
  file: string | null
  tag: string
  id: string
  classes: string[]
  selector: string
  snippet: string
  text: string
  width: number
  height: number
  breadcrumb: Array<{ tag: string; id: string; classes: string[]; loc: string }>
  /** Whether the inspector offers inline text editing for this element
   *  (text-only children + explicit end tag + parent-declared editable file). */
  editable: boolean
  /** Whether the parent holds this document's source writable (drives the
   *  properties panel — style edits need somewhere to land). */
  styleable: boolean
  /** Curated computed-style subset for the properties panel (panel-only;
   *  never sent to the model). */
  styles: Record<string, string>
  /** Raw inline style attribute — lets the panel distinguish declarations
   *  design mode set (bold, unsettable) from merely-computed values. */
  inlineStyle: string
  /** Authored sizing info (panel Size section + resize drag) — null when
   *  the document's source isn't writable. */
  sizing: DesignSizing | null
  /** Computed flex context (panel Layout section) — null when the
   *  document's source isn't writable. */
  layout: DesignLayout | null
  /** Monotonic per-document payload counter — keys the properties panel so
   *  a re-select (e.g. the refresh after a resize drag) re-seeds inputs. */
  seq: number
  /** Structural child-index path from <body> — replayed after a reload to
   *  restore the selection. Null when the element isn't under <body>. */
  path: number[] | null
}

/** Authored (not computed) sizing of a selected element. `from` says where
 *  the width/height declaration lives: the inline style attribute, a
 *  same-origin stylesheet (cascade-lite match), or nowhere ('none' = the
 *  element is auto-sized — computed px would misrepresent that). Parent
 *  dims are CONTENT-box px (the base a percentage resolves against);
 *  rootFontSize powers px↔rem conversion. */
export interface DesignSizing {
  width: { value: string; from: 'inline' | 'sheet' | 'none' }
  height: { value: string; from: 'inline' | 'sheet' | 'none' }
  minWidth: { value: string; from: 'inline' | 'sheet' | 'none' }
  maxWidth: { value: string; from: 'inline' | 'sheet' | 'none' }
  minHeight: { value: string; from: 'inline' | 'sheet' | 'none' }
  maxHeight: { value: string; from: 'inline' | 'sheet' | 'none' }
  boxSizing: 'content-box' | 'border-box'
  display: string
  parentW: number
  parentH: number
  rootFontSize: number
}

/** Computed flex context for the panel's Layout section: the element's own
 *  container props, its flex-item props, and the parent's display/direction
 *  (item controls only make sense inside a flex parent). All computed
 *  values — the panel writes overrides to the inline style like the rest. */
export interface DesignLayout {
  display: string
  position: string
  /** Computed insets — used px for positioned elements, 'auto' otherwise. */
  top: string
  right: string
  bottom: string
  left: string
  zIndex: string
  flexDirection: string
  flexWrap: string
  justifyContent: string
  alignItems: string
  gap: string
  alignContent: string
  flexGrow: string
  flexShrink: string
  flexBasis: string
  alignSelf: string
  opacity: string
  visibility: string
  overflow: string
  parentDisplay: string
  parentFlexDirection: string
}

/** Declarations commit from the inspector (resize drag / sizing menu): the
 *  final inline declarations already applied to the live element — the
 *  editor writes the SAME declarations into the start tag's style
 *  attribute. Empty value = remove the declaration (menu Unset / the
 *  min-max "make responsive" conversion dropping the fixed size). */
export interface DesignResizeCommit {
  loc: string
  file: string | null
  decls: Array<{ prop: string; value: string }>
}

const RESIZE_PROPS = new Set([
  'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height', 'box-sizing',
  // flex-item behavior + alignment (the sizing menu's chips)
  'flex-basis', 'flex-grow', 'flex-shrink', 'align-self',
  // main-axis "push" (margin:auto swallows the free space on that side)
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  // "Clip background to text" (gradient text — webkit prefix still required)
  'background-clip', '-webkit-background-clip', '-webkit-text-fill-color',
  // position move drag (the toolbar's move handle)
  'position', 'top', 'right', 'bottom', 'left',
])

/** Structural reorder from the inspector's "Move in tree" menu — move the
 *  element's source region relative to a target element. The consumer does
 *  the deterministic source surgery. */
export interface DesignReorder {
  loc: string
  targetLoc: string
  mode: 'before' | 'after' | 'append' | 'prepend'
  file: string | null
  /** The moved node's post-move child-index path (from <body>). Lets the
   *  editor re-select it after the reload+re-tag so design mode survives. */
  newPath: number[] | null
}

/** The inspector's PDApp-icon button (#788): ADD one beside the selected
 *  element, or EDIT the selected icon. The consumer owns the dialog and the
 *  source write — this only says which, where, and (for an edit) what the icon
 *  currently carries. */
export interface DesignIconAction {
  action: 'add' | 'edit'
  loc: string
  file: string | null
  /** The icon's `data-papan-icon` attribute, verbatim and UNPARSED. It is page
   *  content, so it is the consumer's to distrust; `null` for an add, and for an
   *  icon that predates the attribute. */
  meta: string | null
}

/** Runtime-validate the untrusted `papan:design-icon-action` payload. */
export function validateDesignIconAction(raw: unknown): DesignIconAction | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  if (d.action !== 'add' && d.action !== 'edit') return null
  if (typeof d.loc !== 'string' || !/^[0-9:]{1,80}$/.test(d.loc)) return null
  // Bounded: it rides a postMessage from a page we do not trust, and the only
  // thing downstream does with it is JSON.parse a spec out of it.
  const meta = typeof d.meta === 'string' && d.meta.length <= 20_000 ? d.meta : null
  return { action: d.action, loc: d.loc, meta, file: typeof d.file === 'string' ? d.file.slice(0, 500) : null }
}

/** The inspector's DELETE button (#788 follow-up): remove the SELECTED element
 *  from the source, whatever it is — a container, a paragraph, an icon.
 *
 *  Deliberately NOT folded into `DesignIconAction`: that channel is about an
 *  icon and carries an icon's metadata, and a delete that arrives on it would
 *  read as "delete the icon" at every call site while actually deleting any
 *  element the author had selected. */
export interface DesignDelete {
  loc: string
  file: string | null
  /** For the confirmation copy only — the tag the author was looking at, as the
   *  inspector labelled it. Untrusted page content; bounded and never parsed. */
  label: string | null
}

/** Runtime-validate the untrusted `papan:design-delete` payload. */
export function validateDesignDelete(raw: unknown): DesignDelete | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  if (typeof d.loc !== 'string' || !/^[0-9:]{1,80}$/.test(d.loc)) return null
  return {
    loc: d.loc,
    file: typeof d.file === 'string' ? d.file.slice(0, 500) : null,
    label: typeof d.label === 'string' ? d.label.slice(0, 120) : null,
  }
}

/** Runtime-validate the untrusted `papan:design-reorder` payload. */
export function validateDesignReorder(raw: unknown): DesignReorder | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  if (typeof d.loc !== 'string' || !/^[0-9:]{1,80}$/.test(d.loc)) return null
  if (typeof d.targetLoc !== 'string' || !/^[0-9:]{1,80}$/.test(d.targetLoc)) return null
  if (d.mode !== 'before' && d.mode !== 'after' && d.mode !== 'append' && d.mode !== 'prepend') return null
  let newPath: number[] | null = null
  if (Array.isArray(d.newPath) && d.newPath.length <= 32) {
    const nums = d.newPath.filter((x): x is number => typeof x === 'number' && Number.isInteger(x) && x >= 0 && x < 10_000)
    if (nums.length === d.newPath.length) newPath = nums
  }
  return { loc: d.loc, targetLoc: d.targetLoc, mode: d.mode, file: typeof d.file === 'string' ? d.file.slice(0, 500) : null, newPath }
}

/** Runtime-validate the untrusted `papan:design-resize` payload. */
export function validateDesignResize(raw: unknown): DesignResizeCommit | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  if (typeof d.loc !== 'string' || !/^[0-9:]{1,80}$/.test(d.loc)) return null
  // Up to 8: a move drag can send box-sizing + width + height size-locks
  // plus the four insets (two pins + two `auto`s).
  if (!Array.isArray(d.decls) || d.decls.length === 0 || d.decls.length > 8) return null
  const decls: DesignResizeCommit['decls'] = []
  for (const rawDecl of d.decls) {
    if (!rawDecl || typeof rawDecl !== 'object') return null
    const r = rawDecl as Record<string, unknown>
    if (typeof r.prop !== 'string' || !RESIZE_PROPS.has(r.prop)) return null
    // Charset admits calc()/var() expressions (parens, spaces, + * /) but
    // no quotes/angle-brackets/semicolons — the value lands inside a
    // style attribute via deterministic string surgery.
    if (typeof r.value !== 'string' || !/^[a-zA-Z0-9(),.%\s+*/-]{0,400}$/.test(r.value)) return null
    decls.push({ prop: r.prop, value: r.value })
  }
  return { loc: d.loc, file: typeof d.file === 'string' ? d.file.slice(0, 500) : null, decls }
}

/** Inline text edit committed in the preview (Phase 2). `loc` is the raw
 *  `data-papan-loc` attribute value — its 5th/6th segments are the inner-
 *  content character offsets in the SOURCE the parent holds; the parent
 *  validates `oldText` against that exact range before applying. */
/** One text-slot replacement from a rich (mixed-content) edit — an exact
 *  SERVE-coord source range plus the old/new text for validation. */
export interface DesignTextEditRegion {
  start: number
  end: number
  oldText: string
  newText: string
}

export interface DesignTextEdit {
  loc: string
  file: string | null
  /** Whole-element (leaf) edit — range derived from `loc`. */
  oldText?: string
  newText?: string
  /** Rich mixed-content edit: per-slot replacements (gaps between child
   *  elements + leaf child interiors). Mutually exclusive with old/newText. */
  edits?: DesignTextEditRegion[]
}

const MAX_DESIGN_EDIT_TEXT = 50_000

/** Runtime-validate the untrusted `papan:design-edit-text` payload. */
export function validateDesignTextEdit(raw: unknown): DesignTextEdit | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  if (typeof d.loc !== 'string' || !/^[0-9:]+$/.test(d.loc)) return null
  const file = typeof d.file === 'string' ? d.file.slice(0, 500) : null
  if (Array.isArray(d.edits)) {
    if (d.edits.length === 0 || d.edits.length > 40) return null
    const edits: DesignTextEditRegion[] = []
    let total = 0
    for (const raw of d.edits) {
      if (!raw || typeof raw !== 'object') return null
      const r = raw as Record<string, unknown>
      if (
        typeof r.start !== 'number' || !Number.isFinite(r.start) || r.start < 0 ||
        typeof r.end !== 'number' || !Number.isFinite(r.end) || r.end < r.start ||
        typeof r.oldText !== 'string' || typeof r.newText !== 'string'
      ) return null
      total += r.oldText.length + r.newText.length
      if (total > MAX_DESIGN_EDIT_TEXT) return null
      edits.push({ start: r.start, end: r.end, oldText: r.oldText, newText: r.newText })
    }
    return { loc: d.loc, file, edits }
  }
  if (typeof d.oldText !== 'string' || typeof d.newText !== 'string') return null
  if (d.oldText.length > MAX_DESIGN_EDIT_TEXT || d.newText.length > MAX_DESIGN_EDIT_TEXT) return null
  return { loc: d.loc, file, oldText: d.oldText, newText: d.newText }
}

/** Runtime-validate the untrusted `papan:design-select` wire payload (it
 *  comes from the sandboxed preview iframe). Null on any malformed shape —
 *  the handler drops it silently. Strings are length-capped so hostile
 *  page JS can't balloon parent state / the agent context. */
export function validateDesignSelect(raw: unknown): DesignElementSelection | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  const loc = d.loc as Record<string, unknown> | null | undefined
  if (!loc || typeof loc !== 'object') return null
  const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null)
  const startLine = n(loc.startLine), startCol = n(loc.startCol), endLine = n(loc.endLine), endCol = n(loc.endCol)
  if (startLine === null || startCol === null || endLine === null || endCol === null) return null
  if (typeof d.tag !== 'string' || typeof d.selector !== 'string' || typeof d.snippet !== 'string') return null
  if (typeof d.locAttr !== 'string' || !/^[0-9:]{1,80}$/.test(d.locAttr)) return null
  const strArr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 20).map((s) => s.slice(0, 100)) : []
  const breadcrumb: DesignElementSelection['breadcrumb'] = []
  if (Array.isArray(d.breadcrumb)) {
    for (const c of d.breadcrumb.slice(0, 10)) {
      if (!c || typeof c !== 'object') continue
      const cc = c as Record<string, unknown>
      if (typeof cc.tag !== 'string' || typeof cc.loc !== 'string') continue
      if (!/^[0-9:]+$/.test(cc.loc)) continue // loc is replayed into a querySelector — digits/colons only
      breadcrumb.push({
        tag: cc.tag.slice(0, 60),
        id: typeof cc.id === 'string' ? cc.id.slice(0, 200) : '',
        classes: strArr(cc.classes),
        loc: cc.loc,
      })
    }
  }
  const styles: Record<string, string> = {}
  if (d.styles && typeof d.styles === 'object') {
    for (const [k, v] of Object.entries(d.styles as Record<string, unknown>).slice(0, 32)) {
      if (/^[a-zA-Z]{1,40}$/.test(k) && typeof v === 'string') styles[k] = v.slice(0, 400)
    }
  }
  let path: number[] | null = null
  if (Array.isArray(d.path) && d.path.length <= 32) {
    const nums = d.path.filter((x): x is number => typeof x === 'number' && Number.isInteger(x) && x >= 0 && x < 10_000)
    if (nums.length === d.path.length) path = nums
  }
  let sizing: DesignSizing | null = null
  if (d.sizing && typeof d.sizing === 'object') {
    const sz = d.sizing as Record<string, unknown>
    const axis = (v: unknown): DesignSizing['width'] => {
      if (!v || typeof v !== 'object') return { value: '', from: 'none' }
      const a = v as Record<string, unknown>
      return {
        value: typeof a.value === 'string' ? a.value.slice(0, 50) : '',
        from: a.from === 'inline' || a.from === 'sheet' ? a.from : 'none',
      }
    }
    const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0)
    sizing = {
      width: axis(sz.width),
      height: axis(sz.height),
      minWidth: axis(sz.minWidth),
      maxWidth: axis(sz.maxWidth),
      minHeight: axis(sz.minHeight),
      maxHeight: axis(sz.maxHeight),
      boxSizing: sz.boxSizing === 'border-box' ? 'border-box' : 'content-box',
      display: typeof sz.display === 'string' ? sz.display.slice(0, 30) : '',
      parentW: num(sz.parentW),
      parentH: num(sz.parentH),
      rootFontSize: num(sz.rootFontSize),
    }
  }
  let layout: DesignLayout | null = null
  if (d.layout && typeof d.layout === 'object') {
    const lo = d.layout as Record<string, unknown>
    const str = (v: unknown): string => (typeof v === 'string' ? v.slice(0, 40) : '')
    layout = {
      display: str(lo.display),
      position: str(lo.position),
      top: str(lo.top),
      right: str(lo.right),
      bottom: str(lo.bottom),
      left: str(lo.left),
      zIndex: str(lo.zIndex),
      flexDirection: str(lo.flexDirection),
      flexWrap: str(lo.flexWrap),
      justifyContent: str(lo.justifyContent),
      alignItems: str(lo.alignItems),
      gap: str(lo.gap),
      alignContent: str(lo.alignContent),
      flexGrow: str(lo.flexGrow),
      flexShrink: str(lo.flexShrink),
      flexBasis: str(lo.flexBasis),
      alignSelf: str(lo.alignSelf),
      opacity: str(lo.opacity),
      visibility: str(lo.visibility),
      overflow: str(lo.overflow),
      parentDisplay: str(lo.parentDisplay),
      parentFlexDirection: str(lo.parentFlexDirection),
    }
  }
  return {
    loc: { startLine, startCol, endLine, endCol },
    locAttr: d.locAttr,
    file: typeof d.file === 'string' ? d.file.slice(0, 500) : null,
    tag: d.tag.slice(0, 60),
    id: typeof d.id === 'string' ? d.id.slice(0, 200) : '',
    classes: strArr(d.classes),
    selector: d.selector.slice(0, 300),
    snippet: d.snippet.slice(0, 4000),
    text: typeof d.text === 'string' ? d.text.slice(0, 500) : '',
    width: n(d.width) ?? 0,
    height: n(d.height) ?? 0,
    breadcrumb,
    editable: d.editable === true,
    styleable: d.styleable === true,
    styles,
    inlineStyle: typeof d.inlineStyle === 'string' ? d.inlineStyle.slice(0, 600) : '',
    sizing,
    layout,
    seq: typeof d.seq === 'number' && Number.isFinite(d.seq) && d.seq >= 0 ? Math.floor(d.seq) : 0,
    path,
  }
}

export interface HtmlViewerProps {
  /** HTML content to render (used as srcDoc in inline mode) */
  content: string
  /** When set, iframe uses src instead of srcDoc (for link navigation in inline mode) */
  src?: string | null
  /** Preview session base URL (e.g. https://preview-golink.papan.ai/s/{sessionId}).
   *  When set, enables session mode with origin isolation + double-buffering. */
  baseUrl?: string | null
  /** Entry file name within the project (e.g. "index.html"). Used with baseUrl. */
  entryFile?: string
  /** The app's OWN query and hash for the entry page (`?room=r1#top`, already encoded) —
   *  a deep link's `/app/<ref>/<page>?…#…` carries them into the frame. Applied to every
   *  load of the entry (first load and reloads). `''` / absent = none. */
  entrySuffix?: string
  /** The app this viewer runs, as the SPA's `/app/<ref>` route opens it — lets the host
   *  answer `pdapp.links.page()` (`papan:pageLink`). Absent (share view, editor previews)
   *  → that call answers `unavailable`. */
  appPage?: AppPageRef | null
  /** File id of the previewed entry file. Lets the SSE-reload handler scope its
   *  "skip if unchanged" optimization to this file's events (vs sibling assets). */
  previewFileId?: string | null
  /** When false, suppresses the SSE external-change reload (file saves by
   *  other users, scripts/SDK writing files) in session mode — mirroring the
   *  editor's "Auto-refresh" toggle so "off" truly means no auto-reload.
   *  The toggle previously gated only the local-typing reload; the SSE
   *  `/__reload` fired unconditionally. Defaults to true (reload on changes). */
  autoRefresh?: boolean
  /**
   * Mini-app folder id. Reserved for the SDK script-run bridge, which was
   * removed in the preview-hardening pass and will be reintroduced by C.1
   * (see the NOTE in the dispatcher wiring below). Callers still pass it so
   * the prop contract is stable for that work; currently unread.
   */
  parentId?: string | null
  /**
   * Debug-log target for THIS preview (mini-app debug log). When set, the
   * viewer forwards captured `papan:console` batches (console/errors/failed
   * fetches from the injected capture shim) to POST /api/debug-log/:kind/:id,
   * and — in inline mode — injects the capture shim into the srcDoc. Set only
   * for author/editor previews of a debug-log-supporting app (a `.miniapp`
   * folder or a loose HTML file); leave null for share-view / version previews
   * so nothing is captured. See docs/plans/2026-07-07_(completed)_MINIAPP_DEBUG_LOG.md.
   */
  debugLogTarget?: { kind: 'miniapp' | 'html'; id: string } | null
  /** Called when user clicks a link inside the iframe */
  onNavigate?: (url: string) => void
  /** When set, `papan:openExternal` messages are delegated here for
   *  parent-side confirmation; otherwise the viewer falls back to a
   *  direct `window.open`. The parent is expected to confirm with the
   *  user and call `window.open` itself. */
  onRequestOpenExternal?: (url: string) => void
  /** When set, `papan:openFile` messages from the iframe are routed
   *  here. The handler receives the requested path verbatim — the
   *  caller is responsible for resolving + opening it. When unset
   *  (e.g. share-view / version-preview surfaces with no workspace
   *  to navigate), the message is dropped with a one-time console
   *  warning. Per-instance source-gated so attacker windows holding
   *  a handle to ours can't spoof. */
  onOpenFile?: (path: string) => void
  /** When set, the typed SDK `papan:openFile` (the `openFileBy*` methods) is routed here.
   *  The relay forwards the wire `target` + the app folder (`parentId`) + advisory `opts`;
   *  the consumer resolves (`max(native, link)`) + opens it (#openfile). When unset, a
   *  `relativePath` target falls back to `onOpenFile(path)` (legacy), else drops with a warn.
   *  Fire-and-forget — no result flows back (no oracle). */
  onOpenFileTarget?: (target: OpenFileTargetWire, ctx: { appFolderId: string | null; opts: OpenFileOptsWire }) => void
  /** #788 — the app asked to LAUNCH another PDApp. No `appFolderId`: the target
   *  names itself by id, and this app's mount context has no bearing on it. */
  onOpenPdappTarget?: (target: OpenPdappTargetWire, ctx: { opts: OpenFileOptsWire }) => void
  /** Design mode (element inspector). When true, the injected inspector in
   *  the preview is armed (`papan:design-mode`) — hover outlines + click-to-
   *  select. Session mode only; the inspector script is only injected for
   *  user-kind preview sessions (see preview-serve.ts). */
  designMode?: boolean
  /** Element selection events from the inspector. `null` = deselected
   *  (Esc in the iframe, or the preview reloaded — a reload re-tags the
   *  source so any prior anchor may be stale). */
  onDesignSelect?: (sel: DesignElementSelection | null) => void
  /** The served path (relative to the preview root) whose SOURCE the host
   *  editor holds writable — forwarded in the arming message so the
   *  inspector only offers inline text editing on that document. Unset =
   *  select-only design mode. */
  designEditableFile?: string | null
  /** Inline text edit committed in the preview (Phase 2). The consumer
   *  applies the deterministic source write-back. */
  onDesignEditText?: (edit: DesignTextEdit) => void
  /** Resize-drag commit (Phase 4a). The drag already styled the live
   *  element; the consumer writes the same declarations to the source. */
  onDesignResize?: (commit: DesignResizeCommit) => void
  /** Ctrl/Cmd+Z pressed INSIDE the preview. The keystroke cannot cross the
   *  iframe boundary on its own, so the injected design script forwards it
   *  and the editor applies its own undo — otherwise a design-mode edit is
   *  only undoable after clicking back into Papan's chrome. */
  onDesignUndo?: (redo: boolean) => void
  /** Structural reorder (Phase 4b, "Move in tree"). The consumer moves the
   *  element's source region relative to the target. */
  onDesignReorder?: (reorder: DesignReorder) => void
  /** #788 — the inspector's add/edit PDApp icon button. */
  onDesignIconAction?: (action: DesignIconAction) => void
  onDesignDelete?: (action: DesignDelete) => void
  /** Save hotkey (Cmd/Ctrl+S) fired while the iframe had focus. A focused
   *  iframe swallows the keystroke, so the bridge forwards it here. When set,
   *  the viewer arms the bridge to capture + forward the hotkey. Editors pass
   *  their save; leave unset (public preview/share) to keep native behavior. */
  onSaveHotkey?: () => void
  /** A local content change was applied to the live preview as a DOM patch
   *  (no reload). The entry is the source-offset shift in SERVE-time
   *  coordinates — the editor feeds it into its design journal so design
   *  write-back anchors stay valid across typing patches. */
  onPreviewPatched?: (e: { pos: number; delta: number }) => void
  /** User-facing notices from the inspector, by CODE (never free text — the
   *  iframe is untrusted; the consumer maps codes to its own copy).
   *  Currently: 'structure-reverted' (a rich edit changed the element
   *  skeleton and was reverted). */
  onDesignNotice?: (code: string) => void
  /** A `scripts.run` outcome the PLATFORM should explain, by CODE (#184 Phase 4).
   *  Same contract as `onDesignNotice` and for a stronger reason: this rides a
   *  response that passed through an untrusted iframe's relay, so the consumer
   *  maps codes to its own copy and never renders anything from here as text.
   *
   *  Raised ONLY when the server attached `authorNotice`, which it does only for
   *  a caller holding `readwrite` on the app folder and only for failures the
   *  app itself cannot explain — so a consumer does not gate this again, and an
   *  end user of a shared PDApp never reaches it. */
  onAppRunNotice?: (notice: { code: string; warnings?: string[]; requestId?: string }) => void
  /** View sync */
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  viewSyncTrigger?: number
  /** Papan Accounts embed context. `'app'` (default, the SPA/previewer) binds
   *  identity to the workspace tenant — no account switching. `'share'` (the
   *  share-link viewer) is an external surface where the embed offers an
   *  account switcher + sign-out. Forwarded to the embed via `?context=`. */
  accountsContext?: 'app' | 'share'
  /** #538 (trial): the drive / workspace window's own Share and Pin, offered as the capsule's Share
   *  and star (the bar asks; the window acts). Omitted ⇒ the bar shows neither. */
  /** #538 — called when the app's page first finishes loading (the window's PDF → PDApp morph waits for it). */
  onFirstLoad?: () => void
  /** #538 — is this viewer's window on screen? A kept-alive window in the background (false) asks
   *  for no consent and fetches nothing for its capsule until it is shown. Default true. */
  screenActive?: boolean
  barActions?: { onShare?: () => void; onLocate?: () => void; onOpenList?: () => void; isPdapp?: boolean; isPinned?: boolean; onTogglePin?: () => void; favoriteAppId?: string; icon?: { spec: import('@papan/types').MiniappIconSpec | null; rev: string | null; refId: string } }
  /** #1408: the app's OWN preview origin (`appPreviewOrigin` from the session mint), when
   *  `baseUrl` is on it. The session frames then get a real origin (camera, microphone,
   *  screen) and every message to and from them is checked against this exact origin (R6).
   *  Ignored unless `baseUrl` is on it — the frames stay opaque. See app-origin-frame.ts. */
  appOrigin?: string | null
  /** (share-view) Share id + the recipient's acting identity (tenant + email), so
   *  the first-party accounts embed authorizes "Manage permissions" via the LINK
   *  GRANT and resolves/keys consent on the SAME identity the app runs as (drive-view
   *  parity). Only meaningful when accountsContext==='share'. */
  shareId?: string
  shareActingTenantId?: string
  shareActingEmail?: string
  /** (share-view) The share TOKEN — sent as `appToken` on the first-party consent
   *  write so /accounts/ppn-consent can resolve the link grant for its canRead gate. */
  shareToken?: string
  /** (share-view) The preview SESSION token. The accounts bar reads consent
   *  status/requirement over THIS (as `x-share-session`) so the bar reflects the
   *  app's ACTUAL principal — an anonymous anyone-with-link session shows "no
   *  identity" instead of the signed-in user's unrelated first-party grant. */
  shareSessionToken?: string
  /** The share session's verified email from its /info (a v2 token seals it, #791); absent
   *  → read from a v1 token. Display only ("you" in the accounts bar). */
  shareVerifiedEmail?: string | null
}

/** The entry's own `?query#hash` as a URL suffix: `''`, or starting `?` / `#`. */
function normalizeEntrySuffix(suffix: string | undefined): string {
  if (!suffix) return ''
  if (!suffix.startsWith('?') && !suffix.startsWith('#')) return ''
  // Papan's own query keys never reach the frame, whoever handed this suffix in
  // (`appOwnUrlSuffix` — the ONE filter, over `PAPAN_RESERVED_QUERY_KEYS`).
  const hashAt = suffix.indexOf('#')
  return appOwnUrlSuffix(hashAt >= 0 ? suffix.slice(0, hashAt) : suffix, hashAt >= 0 ? suffix.slice(hashAt) : '')
}

/** The entry suffix with the reload cache-buster added to its query (before any `#`). */
function withReloadStamp(suffix: string | undefined): string {
  const s = normalizeEntrySuffix(suffix)
  const hashAt = s.indexOf('#')
  const query = hashAt >= 0 ? s.slice(0, hashAt) : s
  const hash = hashAt >= 0 ? s.slice(hashAt) : ''
  return `${query ? `${query}&` : '?'}_t=${Date.now()}${hash}`
}

// Innermost source file id of a (possibly nested) compound mount id
// (`s_<mountId>_<sourceFileId>`), or the id unchanged when it isn't one. Lets
// the reload-event id and the previewed file id match even when they carry
// DIFFERENT representations of the same file (a plain source id vs a compound,
// or different nesting depths) — e.g. a cross-tenant `.shared` / `.chain`
// preview. This FULL-PEELS to the innermost source (parseMountFileId in
// shared/database/mount-id.ts strips one layer); it reuses that file's stable
// `s_<mount>_<src>` parse rules (prefix 's', `_`-free alphanumeric mountId,
// non-empty src). Inlined rather than imported: `@papan/database` is
// server-only and the workspace client mirror lives in `@papan/workspace`,
// which depends on `@papan/collab` — importing it here would be a package
// cycle. The 24-iteration bound is a cyclic-id runaway guard.
function mountSourceFileId(id: string): string {
  let cur = id
  for (let depth = 0; depth < 24; depth++) {
    const i1 = cur.indexOf('_')
    if (i1 < 0 || cur.slice(0, i1) !== 's') break
    const i2 = cur.indexOf('_', i1 + 1)
    if (i2 < 0) break
    const mountId = cur.slice(i1 + 1, i2)
    const sourceFileId = cur.slice(i2 + 1)
    if (mountId === '' || sourceFileId === '' || !/^[A-Za-z0-9]+$/.test(mountId)) break
    cur = sourceFileId
  }
  return cur
}

/** One PROACTIVE consent prompt per app folder, per document (#525).
 *
 *  `consentPromptedRef` is a `useRef`, so it arms exactly one `HtmlViewer`.
 *  The workspace keeps decks MOUNTED rather than unmounting them, so opening
 *  an app and also opening its `index.html` in the editor leaves two viewers
 *  alive at once. Each fetches the requirement, each finds the fields
 *  undecided, and each opens its own gate.
 *
 *  Answering one neither closes nor refreshes the other, and the survivor
 *  still believes it is a FIRST prompt — so its optional switches are seeded
 *  ON. Answering that stale dialog rewrites a record it cannot see: it flips a
 *  field the user just turned off, and, worse, turns an outright DECLINE into
 *  a full grant. Reproduced both ways.
 *
 *  Module scope is the right scope, because the defect is one document
 *  showing two prompts for one app. Two browser TABS are two documents and
 *  still race; closing that needs the WRITE to be conditional on the record
 *  the dialog was seeded from, which is a server-side change and not this.
 *
 *  MANAGE is deliberately NOT fenced. It is user-initiated and re-reads the
 *  requirement on every open, and a fence there would make the button on the
 *  second surface do nothing — the exact defect #525 exists to remove.
 */
const consentGateOpen = new Map<string, symbol>()

/** Release only if WE still hold it. A superseded run's promise can settle long
 *  after another run has taken the fence (the embed relay resolves `null` on a
 *  600s timeout), and an unconditional delete would hand the door back while a
 *  live dialog still guards it. */
function releaseFence(appFolderId: string, token: symbol): void {
  if (consentGateOpen.get(appFolderId) === token) consentGateOpen.delete(appFolderId)
}

// Wire shape of the SDK `papan:openFile` target (mirrors `@papan/sdk`'s OpenFileTarget;
// kept local so features/collab doesn't depend on the SDK package). Runtime-validated in the
// handler since it arrives from the untrusted app iframe.
export type OpenFileTargetWire =
  | { kind: 'relativePath'; path: string }
  | { kind: 'drivePath'; path: string }
  | { kind: 'fileId'; fileId: string; fileTenantId: string }
export interface OpenFileOptsWire {
  workspaceId?: string
  // Advisory destination LENS (mirrors the SDK's `OpenTenant`): 'fileTenant'/'auto'
  // default to the file's owner tenant, 'activeTenant' pins to the current one,
  // 'linkTenant' is an explicit share-link grant. No concrete `tenantId` — the
  // foreign tenant is named only by a `byId` target's `fileTenantId`.
  openTenant?: 'auto' | 'activeTenant' | 'fileTenant' | 'linkTenant'
}

/** Wire shape of the SDK `papan:openPdapp` target (#788) — LAUNCH an app, which
 *  is a different act from opening a file, so it is a different message with its
 *  own kind. Runtime-validated like its twin: it arrives from the untrusted app
 *  iframe. */
export type OpenPdappTargetWire = { kind: 'folderId'; folderId: string; tenantId: string }

/**
 * Drive ids as the drive mints them — the same shape the SERVER consumers of
 * these targets already insist on (`ID_RE` in `app-deep-link.ts` and in
 * share-links' `session-routes.ts`).
 *
 * The in-Papan consumers did NOT, and they are the ones that interpolate an id
 * straight into a `window.open` route. Checking shape here rather than at each
 * consumer means a new consumer inherits it instead of having to remember.
 */
const WIRE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/

/** Validate the untrusted wire target of `papan:openPdapp`, or null (→ dropped). */
export function validateOpenPdappTarget(raw: unknown): OpenPdappTargetWire | null {
  if (!raw || typeof raw !== 'object') return null
  const t = raw as Record<string, unknown>
  if (t.kind === 'folderId' && typeof t.folderId === 'string' && typeof t.tenantId === 'string'
      && WIRE_ID_RE.test(t.folderId) && WIRE_ID_RE.test(t.tenantId)) {
    return { kind: 'folderId', folderId: t.folderId, tenantId: t.tenantId }
  }
  return null
}

/** Validate the untrusted wire `target` from the app iframe into a typed OpenFileTargetWire,
 *  or null if malformed/unrecognized (→ the relay drops it silently). */
export function validateOpenFileTarget(raw: unknown): OpenFileTargetWire | null {
  if (!raw || typeof raw !== 'object') return null
  const t = raw as Record<string, unknown>
  if (t.kind === 'relativePath' && typeof t.path === 'string') return { kind: 'relativePath', path: t.path }
  if (t.kind === 'drivePath' && typeof t.path === 'string') return { kind: 'drivePath', path: t.path }
  // Same shape rule as the pdapp target: both ids reach a `window.open` route.
  // (`relativePath`/`drivePath` above are PATHS, resolved by the drive rather
  // than interpolated, so they are deliberately not id-shaped.)
  if (t.kind === 'fileId' && typeof t.fileId === 'string' && typeof t.fileTenantId === 'string'
      && WIRE_ID_RE.test(t.fileId) && WIRE_ID_RE.test(t.fileTenantId)) {
    return { kind: 'fileId', fileId: t.fileId, fileTenantId: t.fileTenantId }
  }
  return null
}

// ─── Inline mode helpers (srcDoc) ─────────────────────────────────────────

/**
 * The backdrop behind every preview iframe (session A/B and inline). The page is
 * an opaque-origin sandbox, so Papan's theme can't reach into it — a page with
 * no background of its own shows whatever is behind the iframe. Session iframes
 * used to be transparent (the dark theme card showed through, under the page's
 * default BLACK text) while inline was white, so the same file looked "themed"
 * or "light" depending on the mode. One value, the browser-default canvas: the
 * page looks as it would in a normal tab. Its own background and its
 * `color-scheme` / `prefers-color-scheme` dark styles still paint over this.
 *
 * Session buffers get it only once their first real page has LOADED: before
 * that the frame is an empty transparent document, and a white backdrop would
 * flash between the host's themed loading placeholder and the page. Reloads
 * load into the hidden buffer and swap on load, so they never show it empty.
 * Inline srcDoc renders without a network round-trip, so it is always on.
 */
const PREVIEW_BACKDROP = '#ffffff'

/** #1316 — the terminal status a detached run's poll ends on once its session ended (the app hears `session_ended`). */
const SESSION_ENDED_RUN_STATUS: RunStatusBody = { state: 'refused', result: { ok: false, error: 'session_ended' } }

function injectScript(html: string, script: string): string {
  const i = html.lastIndexOf('</body>')
  return i !== -1 ? html.slice(0, i) + script + html.slice(i) : html + script
}

// ─── Component ────────────────────────────────────────────────────────────

export const HtmlViewer = forwardRef<HtmlViewerHandle, HtmlViewerProps>(
  function HtmlViewer({ content, src, baseUrl, entryFile, entrySuffix, appPage, previewFileId, autoRefresh = true, parentId, debugLogTarget, onNavigate, onRequestOpenExternal, onOpenFile, onOpenFileTarget, onOpenPdappTarget, designMode = false, onDesignSelect, designEditableFile, onDesignEditText, onDesignResize, onDesignUndo, onDesignReorder, onDesignIconAction, onDesignDelete, onSaveHotkey, onPreviewPatched, onDesignNotice, onAppRunNotice, viewSync, viewSyncId, viewSyncTrigger, accountsContext = 'app', barActions, screenActive = true, onFirstLoad, appOrigin, shareId, shareActingTenantId, shareActingEmail, shareToken, shareSessionToken, shareVerifiedEmail }, ref) {
    // Double-buffering state for session mode
    const [activeIdx, setActiveIdx] = useState<0 | 1>(0)
    // Per buffer: has a real page loaded yet? Until then the frame holds an
    // empty, transparent document — keep it transparent so the host's themed
    // placeholder shows, not a white box (see PREVIEW_BACKDROP).
    const [bufferLoaded, setBufferLoaded] = useState<readonly [boolean, boolean]>([false, false])
    const [_pendingLoad, setPendingLoad] = useState(false)
    const iframe0Ref = useRef<HTMLIFrameElement>(null)
    const iframe1Ref = useRef<HTMLIFrameElement>(null)
    const onNavigateRef = useRef(onNavigate)
    onNavigateRef.current = onNavigate
    const onRequestOpenExternalRef = useRef(onRequestOpenExternal)
    onRequestOpenExternalRef.current = onRequestOpenExternal
    const onOpenFileRef = useRef(onOpenFile)
    onOpenFileRef.current = onOpenFile
    const onOpenFileTargetRef = useRef(onOpenFileTarget)
    onOpenFileTargetRef.current = onOpenFileTarget
    const onOpenPdappTargetRef = useRef(onOpenPdappTarget)
    onOpenPdappTargetRef.current = onOpenPdappTarget
    // Design mode: refs so the stable message handlers see live values.
    const designModeRef = useRef(designMode)
    designModeRef.current = designMode
    const onDesignSelectRef = useRef(onDesignSelect)
    onDesignSelectRef.current = onDesignSelect
    const designEditableFileRef = useRef(designEditableFile)
    designEditableFileRef.current = designEditableFile
    const onDesignEditTextRef = useRef(onDesignEditText)
    onDesignEditTextRef.current = onDesignEditText
    const onDesignUndoRef = useRef(onDesignUndo)
    onDesignUndoRef.current = onDesignUndo
    const onDesignResizeRef = useRef(onDesignResize)
    onDesignResizeRef.current = onDesignResize
    const onDesignReorderRef = useRef(onDesignReorder)
    onDesignReorderRef.current = onDesignReorder
    const onDesignIconActionRef = useRef(onDesignIconAction)
    onDesignIconActionRef.current = onDesignIconAction
    const onDesignDeleteRef = useRef(onDesignDelete)
    onDesignDeleteRef.current = onDesignDelete
    const onSaveHotkeyRef = useRef(onSaveHotkey)
    onSaveHotkeyRef.current = onSaveHotkey
    const onPreviewPatchedRef = useRef(onPreviewPatched)
    onPreviewPatchedRef.current = onPreviewPatched
    const onDesignNoticeRef = useRef(onDesignNotice)
    onDesignNoticeRef.current = onDesignNotice
    const onAppRunNoticeRef = useRef(onAppRunNotice)
    onAppRunNoticeRef.current = onAppRunNotice
    const entryFileRef = useRef(entryFile)
    entryFileRef.current = entryFile
    const syncSeqRef = useRef(0)
    // #openfile #11 — openFile dedupe: LEADING-EDGE, keyed by target. Map of recently-fired
    // target key → its expiry timer. A key present = "just fired this exact target" → swallow
    // repeats (a double-click / re-render); distinct targets are independent. Bounds a tight
    // app loop from thrashing the resolve endpoint without dropping legit distinct opens.
    const openFileFiredRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
    const parentIdRef = useRef(parentId)
    parentIdRef.current = parentId
    const appPageRef = useRef(appPage)
    appPageRef.current = appPage
    // #1408: the exact origin the session frames run on, when it is the app's own preview origin
    // (null = opaque frames, origin 'null'). Every post to them targets it, and every message from
    // them must come from it (R6) — a frame the app navigated elsewhere gets nothing.
    const frameAppOrigin = frameAppOriginFor(appOrigin, baseUrl)
    const frameAppOriginRef = useRef(frameAppOrigin)
    frameAppOriginRef.current = frameAppOrigin
    const frameTargetOrigin = () => frameAppOriginRef.current ?? '*'
    // ─── Debug-log batching (mini-app debug log — parent-side receiver) ─────
    // The injected capture shim already batches in-iframe; we coalesce those
    // batches once more here (debounce + max-size) so a chatty app produces at
    // most one POST/second, then flush on unmount. Target lives in a ref so the
    // stable message handler always sees the current app without re-subscribing.
    const debugLogTargetRef = useRef(debugLogTarget)
    debugLogTargetRef.current = debugLogTarget
    const debugLogBufferRef = useRef<unknown[]>([])
    const debugLogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const debugLogLastFlushRef = useRef(0)
    // Preview base, readable inside the stable flush callback — used to turn
    // the shim's raw `page` pathname into a path relative to the app root
    // (`file`) so a miniapp's consolidated log attributes entries per file.
    const debugLogBaseUrlRef = useRef(baseUrl)
    debugLogBaseUrlRef.current = baseUrl
    const flushDebugLog = useCallback(() => {
      if (debugLogTimerRef.current) { clearTimeout(debugLogTimerRef.current); debugLogTimerRef.current = null }
      debugLogLastFlushRef.current = Date.now()
      const target = debugLogTargetRef.current
      const buffered = debugLogBufferRef.current
      if (!target || !buffered.length) { debugLogBufferRef.current = []; return }
      debugLogBufferRef.current = []
      // Map the shim's `page` (raw iframe pathname) → `file` (path relative to
      // the app root) for miniapp targets; drop `page` either way (Mode A is a
      // single file — no attribution needed).
      const entries = buffered.map((raw) => {
        const { page, ...e } = (raw ?? {}) as { page?: unknown } & Record<string, unknown>
        if (target.kind !== 'miniapp' || typeof page !== 'string' || !page) return e
        try {
          const base = debugLogBaseUrlRef.current
          const prefix = base ? new URL(base, window.location.origin).pathname.replace(/\/+$/, '') : ''
          // Only attribute when the served page path is genuinely UNDER the app's
          // base prefix. If it isn't (a session `/s/…` URL, an in-app redirect,
          // a cross-origin page), leave the entry unattributed rather than
          // tagging it with a bogus full path — a wrong `file` spawns a phantom
          // per-file section and never matches the real per-file view/clear.
          if (!prefix || !page.startsWith(prefix)) return e
          const rel = page.slice(prefix.length).replace(/^\/+/, '')
          return rel ? { ...e, file: decodeURIComponent(rel) } : e
        } catch { return e }
      })
      // Fire-and-forget; a rejected POST (e.g. lost write perm) just drops the
      // batch — the debug log is best-effort and must never disrupt the preview.
      // raw (#1316): best-effort telemetry about what already ran — never shown, nothing to drop
      fetch(`${getApiBase()}/api/debug-log/${target.kind}/${encodeURIComponent(target.id)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
        body: JSON.stringify({ entries: entries.slice(0, 200) }),
      }).catch(() => { /* best-effort */ })
    }, [])
    const enqueueDebugLog = useCallback((entries: unknown[]) => {
      if (!debugLogTargetRef.current || !Array.isArray(entries) || !entries.length) return
      const buf = debugLogBufferRef.current
      for (const e of entries) { if (buf.length < 200) buf.push(e) }
      if (buf.length >= 100) { flushDebugLog(); return }
      // A batch carrying a warn/error (the shim sends those urgently) goes out at
      // once when nothing went in the last second — leading edge, so this adds at
      // most ~1 POST/s (the >=100-entry flush above is the older, separate bound). The agent's preview tools read the log right after an
      // eval; a 1s hold let an eval-triggered error or the #1002 form warning miss
      // that read.
      const urgent = entries.some((e) => {
        const lvl = (e as { lvl?: unknown } | null)?.lvl
        return lvl === 'error' || lvl === 'warn'
      })
      // Even when a timer is already armed (e.g. by a fresh load's session_start
      // batch): flushDebugLog clears it and sends the whole buffer.
      if (urgent && Date.now() - debugLogLastFlushRef.current >= 1000) { flushDebugLog(); return }
      if (!debugLogTimerRef.current) debugLogTimerRef.current = setTimeout(flushDebugLog, 1000)
    }, [flushDebugLog])
    // Flush any buffered entries when the viewer unmounts.
    useEffect(() => () => flushDebugLog(), [flushDebugLog])
    // Latest autoRefresh value, read inside the long-lived SSE handler closure
    // so toggling the editor's "Auto-refresh" off immediately stops external
    // reloads without tearing down + reconnecting the EventSource.
    const autoRefreshRef = useRef(autoRefresh)
    autoRefreshRef.current = autoRefresh
    // Consent dialog state (C.6 / Mode A-with-manifest + Mode B).
    // Surfaces a Papan-native modal outside the iframe so the HTML
    // author's CSS/JS cannot tamper with the prompt.
    // The dialog's OWN shape, imported rather than restated. This state is one
    // hop in a hand-forwarded chain, and every hop that re-declared the shape
    // has silently swallowed a field: the embed's message handler dropped
    // `granted`/`declined`, and `SdkConsentDialog` dropped `newFields` — which
    // turned every in-host gate into a manage screen with no Decline. This copy
    // was already missing `declined`, and only worked because the value rides a
    // spread that TypeScript never looks at. Sharing the type makes the next
    // omission a compile error instead of a silent behaviour change.
    const [consentRequest, setConsentRequest] = useState<SdkConsentRequest | null>(null)
    // #605 file handoff: Papan's picker for the app's pickAndRead / pickAndSave.
    // `shownKey`: an ask from the frame a reload swap takes off screen ends.
    const handoff = useHandoffRelay({ baseUrl, accountsContext, shownKey: activeIdx })
    // A drag from Papan's own page onto the app: the browser won't deliver it
    // into the app's frame, so it is caught over the frame and relayed.
    // Frames whose page said hello through the SDK (`papan:handshake`).
    const sdkWindowsRef = useRef(new WeakSet<Window>())
    const dragCatcher = useHandoffDragRelay({
      enabled: !!baseUrl && !handoff.framesHidden,
      frame: () => (activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current),
      runsSdk: (win) => sdkWindowsRef.current.has(win),
    })
    // `Omit<…, 'resolve'>` rather than a hand-written twin: this is the same
    // seam as the state above, and a restated shape here drops a field with no
    // compile error and no visible symptom.
    const openConsentDialog = useCallback((params: Omit<SdkConsentRequest, 'resolve'>) => {
      return new Promise<{ decision: 'allow_all' | 'required_only' | 'decline'; grantedFields: string[] } | null>((resolve) => {
        setConsentRequest({ ...params, resolve })
      })
    }, [])
    /** The consent requirement URL for THIS surface.
     *
     *  `shareId` is load-bearing on a shared project link. The endpoint builds
     *  its permission session from it (`sessionFromClaims` → `shareLinkIds`);
     *  without it the check falls back to NATIVE access only, so a recipient
     *  who reaches the app purely through the share link is `forbidden` — and
     *  both readers below bail on a non-ok response, which reads to the user as
     *  the button doing nothing. The accounts embed has always passed it on its
     *  own fetch; the host's two never did, which did not show while the button
     *  was hidden on a share with no live requirement (#525 B).
     *
     *  Omitted outside the share context: a member's native permission is what
     *  should answer there, and a stale id would only narrow it. */
    const consentRequirementUrl = useCallback((appFolderId: string) => {
      const q = new URLSearchParams({ appFolderId })
      if (accountsContext === 'share' && shareId) q.set('shareId', shareId)
      return `${getApiBase()}/api/v1/sdk/consent/requirement?${q.toString()}`
    }, [accountsContext, shareId])

    /** The record, for a dialog whose trigger did not carry it.
     *
     *  The reactive gate opens on a `scripts.run` / `whoami` 412, and that body
     *  carries only what the manifest DECLARES — never what the user has
     *  decided. A dialog opened without the record defaults every optional
     *  field on, so pressing Allow rewrites the lot and a field the user had
     *  DECLINED comes back granted, from a control they never touched.
     *
     *  Best-effort by design: this runs at a moment the app is already blocked,
     *  so a failed read must not also swallow the prompt. Returning nothing
     *  falls back to the old all-on seeding, which is wrong in the same old
     *  way but no worse — whereas throwing would leave the user stuck.
     *
     *  #1316: a read dropped because its session ended also comes back empty — the callers check their session right
     *  after it and open nothing then (a dialog shown would ask the NEXT session). */
    const consentRecord = useCallback(async (appFolderId: string, required: string[], optional: string[]) => {
      try {
        const start = currentSessionSignal()   // #1316 review round 1: the body too is read in this session
        const res = await sessionFetch(consentRequirementUrl(appFolderId), { credentials: 'include', headers: getDefaultHeaders() })
        if (!res.ok) return {}
        const r = await readInSession<{ alreadyGranted?: string[] | null; alreadyDeclined?: string[] | null }>(res, start)
        if (!Array.isArray(r.alreadyGranted)) return {}
        const granted = r.alreadyGranted
        const declined = r.alreadyDeclined ?? []
        // `newFields` must ride along, and it is not decoration. The dialog reads
        // its ABSENCE — beside a known record — as "this is a management view",
        // which flips the footer to Cancel / Save and drops Decline. Handing the
        // record over without it therefore turns a consent REQUEST on a blocked
        // app into a manage screen with no way to refuse.
        return {
          granted,
          declined,
          newFields: [...required, ...optional].filter((f) => !granted.includes(f) && !declined.includes(f)),
        }
      } catch { return {} }
    }, [consentRequirementUrl])
    // Ref'd for the same reason as `openManageConsentRef` / `triggerReloadRef`
    // beside it: the reactive 412 sites live inside a long-lived block whose
    // dependency list does not name this, and unlike `openConsentDialog` (deps
    // `[]`, stable) this one changes with `shareId`. Captured directly, a share
    // session that resolved its id late would keep calling the OLD closure,
    // whose URL omits `shareId` — the read 403s, the record comes back empty,
    // and the prompt quietly falls back to defaulting every switch on.
    const consentRecordRef = useRef(consentRecord)
    consentRecordRef.current = consentRecord

    // R13 — bar "Manage permissions" delegates to the host (was an in-frame embed dialog
    // the bar could be framed + clickjacked into). Re-fetch the requirement server-side
    // (never trust the bar's strings — R21 #140) and render the host's own SdkConsentDialog.
    const openManageConsent = useCallback(async () => {
      // Whole-app clickjack guard: refuse if the host SPA itself is framed.
      if (typeof window !== 'undefined' && window.top !== window.self) return
      if (!parentId) return
      let req: { manifest?: boolean; required?: string[]; optional?: string[]; alreadyGranted?: string[] | null; alreadyDeclined?: string[] | null; appName?: string; appOwnerName?: string | null } | null = null
      const start = currentSessionSignal()   // #1316 review round 1: the bodies too are read in this session
      try {
        const res = await sessionFetch(consentRequirementUrl(parentId), { credentials: 'include', headers: getDefaultHeaders() })
        if (res.ok) req = await readInSession(res, start)
      } catch (err) {
        // #1316: the session it was asked for ended — no dialog for the person now here
        if (isSessionEndedError(err)) return null
        /* else fall through to the record read below */
      }
      const required = Array.isArray(req?.required) ? req.required : []
      const optional = Array.isArray(req?.optional) ? req.optional : []
      // THE BUTTON MUST NEVER DO NOTHING — including when this read fails.
      //
      // It used to `return` on any non-ok response, which is a silent dead end:
      // an author saving a malformed `manifest.json` makes the endpoint 400 on
      // every call, so the button was permanently inert while the record still
      // held the user's identity. The bar was separately changed to KEEP the
      // button when the answer is unknown — but that only preserved a route
      // that no-ops.
      //
      // The same read also fails to carry the record when the manifest is
      // MISSING rather than malformed: `NO_MANIFEST` answers 200 with
      // `alreadyGranted: null`, before the endpoint looks at the row. The bar
      // shows the button there precisely BECAUSE the record is non-empty (it
      // reads the row directly), so the two disagreed and the dialog opened
      // saying the app asks for nothing, Close-only, record unreachable.
      //
      // Both are "the requirement could not tell us what is on record", so both
      // take the same fallback: ask the record endpoint, which is not
      // manifest-gated. Failing that too, `undefined` keeps the honest "we do
      // not know" state rather than claiming the app holds nothing.
      let granted = req?.alreadyGranted ?? undefined
      let declined = req?.alreadyDeclined ?? undefined
      if (granted === undefined) {
        try {
          const q = new URLSearchParams({ appFolderId: parentId })
          if (accountsContext === 'share' && shareId) q.set('shareId', shareId)
          const rec = await sessionFetch(`${getApiBase()}/api/v1/sdk/consent?${q.toString()}`, { credentials: 'include', headers: getDefaultHeaders() })
          if (rec.ok) {
            const body = await readInSession<{ existing?: { grantedFields?: string[]; declinedFields?: string[] } | null }>(rec, start)
            if (body.existing && Array.isArray(body.existing.grantedFields)) {
              granted = body.existing.grantedFields
              declined = Array.isArray(body.existing.declinedFields) ? body.existing.declinedFields : []
            }
          }
        } catch (err) {
          if (isSessionEndedError(err)) return null // #1316 — as above
          /* else still unknown — the dialog says so rather than guessing */
        }
      }
      // NO early return on an empty requirement (#525).
      //
      // This used to `return null` here, so "Manage permissions" was a button
      // that fetched, found the manifest asking for nothing, and silently did
      // nothing — while the consent record still held whatever the user had
      // granted before the author edited `wants_identity` out.
      //
      // The manifest says what the app CAN see; this dialog says what it HAS.
      // The second question always has an answer, so the dialog always opens —
      // it renders the held-but-unrequested fields and offers Withdraw, and
      // says so plainly when there is genuinely nothing.
      // Returns the decision (or null on dismiss/error) so the caller can reload the app
      // after a real change — preserving the old in-frame flow's post-consent reload.
      const appName = req?.appName ?? 'This PDApp'
      const answer = await openConsentDialog({
        appFolderId: parentId, appName, appOwnerName: req?.appOwnerName ?? null,
        // `?? undefined`, NOT `?? []` — the endpoint returns null both for "no
        // decision on record" and for "did not look" (the manifest-missing early
        // return). Collapsing those to an empty array would make the dialog claim
        // the app holds nothing when it may simply not know.
        required, optional,
        // A REFUSAL is a decision the record holds too — resolved above, from
        // the requirement read or the record endpoint it falls back to.
        granted,
        declined,
      })
      // #538 — a decision that leaves a REQUIRED field ungranted (Decline all, or a declined
      // required field) blocks the PDApp. Said here, where the requirement is known: the caller's
      // reload re-arms the gate and clears the panel, and the reloaded app is not guaranteed to
      // make the call whose 412 would raise it again — so it ran on, looking allowed.
      if (answer) {
        const grantedNow = new Set(answer.decision === 'decline' ? [] : (answer.grantedFields || []))
        if (required.some((f) => !grantedNow.has(f))) return { ...answer, blocked: { appName, required } }
      }
      return answer
    }, [parentId, openConsentDialog, consentRequirementUrl, accountsContext, shareId])
    // Consent re-prompt + block-panel state (Mode A-with-manifest / Mode B).
    // `consentPromptedRef` arms the consent dialog ONCE per page load; a reload
    // (SSE change, manual refresh, or the block panel's "Review" button)
    // re-arms it. `consentBlocked` drives a Papan-native block panel shown when
    // REQUIRED consent is declined, so the app surfaces "needs permission"
    // instead of rendering as a dead page. Required consent is a precondition,
    // so re-prompting on a fresh load is correct (not a nag); optional declines
    // never reach here — the scripts-run gate only blocks on required fields.
    const consentPromptedRef = useRef(false)
    /** The consent fence this viewer holds, if any.
     *
     *  A REF — not a `let` inside the gate effect, and not an "unmounted" flag.
     *  Both were wrong:
     *
     *  - A per-run `let` belongs to ONE effect run. A dependency change
     *    (`manifestVersion` bumps on any file event in the app folder) starts a
     *    new run whose variable is null, leaving the token reachable only from
     *    the old run's `finally` — which never fires while its dialog is still
     *    open. The fence was then stranded for the life of the page, and the
     *    app could never prompt again.
     *  - An `unmountedRef` set by a cleanup is the trap CLAUDE.md documents:
     *    `<StrictMode>` runs mount → cleanup → mount, so it latches true during
     *    the FIRST mount and the guard reads inverted for the rest of the
     *    component's life — releasing on every dep change, in exactly the
     *    environment this gets verified in.
     *
     *  A ref survives re-runs, and the `[]`-deps cleanup below is a real unmount
     *  hook needing no flag. StrictMode's synthetic cleanup is harmless: it runs
     *  before any fetch resolves, so nothing is held yet. */
    const fenceHeldRef = useRef<{ id: string; token: symbol } | null>(null)
    const takeFence = useCallback((id: string): symbol | null => {
      if (consentGateOpen.has(id)) return null
      const token = Symbol('consent-gate')
      consentGateOpen.set(id, token)
      fenceHeldRef.current = { id, token }
      return token
    }, [])
    const dropFence = useCallback((id: string, token: symbol) => {
      releaseFence(id, token)
      if (fenceHeldRef.current?.token === token) fenceHeldRef.current = null
    }, [])
    useEffect(() => () => {
      const held = fenceHeldRef.current
      if (held) { releaseFence(held.id, held.token); fenceHeldRef.current = null }
    }, [])
    const [consentBlocked, setConsentBlocked] = useState<{
      appName: string
      required: string[]
      /** WHY this app is not running, because the panel says so in words and
       *  only one of these is a decision the user made.
       *
       *  `declined` — they answered no (or dismissed the prompt), which is what
       *    the panel has always described.
       *  `pending`  — another mounted viewer holds the consent fence and is
       *    asking right now. The panel used to say "and you declined" here too,
       *    telling someone they had refused something they were still being
       *    asked about. Same words, wrong person, no way to tell them apart.
       *  `reentry`  — #796: the app REQUIRES identity and this visit has none
       *    (an anonymous public-link visitor). Not a consent anyone can grant
       *    here — consent needs an account — so the panel offers SIGN-IN (the
       *    share viewer's own, via SHARE_SIGN_IN_EVENT), never the dialog. */
      reason: 'declined' | 'pending' | 'reentry'
    } | null>(null)
    /** Mirror, so the gate effect can tell "there was a panel up" from "there
     *  wasn't" without listing `consentBlocked` in its deps — which would
     *  re-run the whole requirement fetch every time a panel appeared. */
    const consentBlockedRef = useRef(consentBlocked)
    consentBlockedRef.current = consentBlocked
    // Papan Accounts embed (previewer): the SPA renders the SAME embed.html as a
    // SIBLING of the app iframe — a real same-origin frame (cookies + window.top
    // + isolated from the app), which a frame nested inside the sandboxed app
    // can't be (opaque origin). It resizes between a bottom bar and a full
    // overlay on the embed's request, and reloads the app when consent changes.
    const embedRef = useRef<HTMLIFrameElement>(null)
    const [embedExpanded, setEmbedExpanded] = useState(false)
    // User dismissed the Accounts bar (the "×" in the embed). Hide it until the
    // next mount/reload — same dismiss UX as ppn.gg, here for previewer/share.
    const [embedHidden, setEmbedHidden] = useState(false)
    // #538 (trial): the floating capsule's size, as the bar reports it.
    const [barBox, setBarBox] = useState<{ w: number; h: number }>({ w: 360, h: 48 })
    const onFirstLoadRef = useRef(onFirstLoad)
    onFirstLoadRef.current = onFirstLoad
    const screenActiveRef = useRef(screenActive)
    screenActiveRef.current = screenActive
    const barActionsRef = useRef(barActions)
    barActionsRef.current = barActions
    // The app's in-iframe SDK hit a TERMINAL realtime failure (today only the
    // per-user WS cap, code 4008) and posted `papan:realtime-unavailable`. The
    // app's own `onConnectionError` can't touch this trusted frame, so the host
    // paints the banner. Dismissible; re-armed on the next mount/reload.
    const [realtimeCapped, setRealtimeCapped] = useState(false)
    // #1408 stage 1: the app asked for the camera, microphone or screen and the
    // opaque-origin frame refused it (the storage shim posts
    // `papan:needs-app-origin`). The host paints the hint and, for a person in
    // their own drive, offers the app's own link, where those work. Re-armed on
    // the next mount/reload, like the realtime banner.
    const [needsAppOrigin, setNeedsAppOrigin] = useState<null | 'media' | 'screen'>(null)
    const [appLinkError, setAppLinkError] = useState<string | null>(null)
    // NOTE: embedTenant removed (#187) — the share-context embed is display-only;
    // account/tenant switching moved to the native top-level chrome.
    // The gate no longer relays to the embed. It used to, "so there's ONE
    // consent surface" — but the embed renders inside this viewer's own
    // subtree, and a backgrounded app deck is `opacity-0 pointer-events-none`,
    // so once a fence made exactly one viewer responsible for asking, a hidden
    // winner mounted the prompt invisible and click-through. The host dialog is
    // portaled to `document.body`, so it is visible whichever viewer owns it —
    // and it is the single surface now, which is what that goal actually wanted.
    // (`embed.html`'s `openGateDialog` receiver is left in place: it is the
    // cross-host contract, and nothing here posts to it any more.)
    // Relay a scripts.run 412/403 gate to the embed, which renders the SAME
    // consent dialog as its bar "manage" path and replies the decision. Resolves
    // with the same shape as the in-host `openConsentDialog` so the run flow is
    // identical regardless of which surface handled it.

    // After a consent decision the WRITE lands server-side but the Accounts bar (a
    // sibling iframe) still holds its load-time `state.granted`, so the masked email/org
    // wouldn't update until a full tab reload. Ping the embed to re-read + re-render.
    // Ref'd so the stable message/run handlers can call it without a re-render dep.
    // #538 (trial): the SPA's own capsule re-reads what the PDApp may see when this bumps.
    const [capsuleRefresh, setCapsuleRefresh] = useState(0)
    const refreshAccountsBar = useCallback(() => {
      setCapsuleRefresh((n) => n + 1)
      const w = embedRef.current?.contentWindow
      if (!w) return
      try { w.postMessage({ source: 'papan-host', type: 'papan:accounts:refresh' }, window.location.origin) } catch { /* embed gone */ }
    }, [])
    const refreshAccountsBarRef = useRef(refreshAccountsBar)
    refreshAccountsBarRef.current = refreshAccountsBar

    // Re-arm the consent gate when ANY change touches this app folder under an OPEN
    // viewer — create/update/move/delete/restore, and renames (the event carries the
    // NEW name, so we match by FOLDER, not filename — that's the only way to catch a
    // manifest.json renamed *away*). Editing `wants_identity` then re-prompts live
    // (new/re-added field) or reflects an auto-dropped one, with no manual reload.
    // Debounced so a multi-file save coalesces into one re-check; idempotent — the
    // gate only actually re-prompts when the identity fields changed.
    const onFileEvent = useRealtimeOptional()?.onFileEvent
    /**
     * #471 — the run-completion push, held in a ref.
     *
     * The `papan:run` message handler is installed once and is not re-created
     * when the realtime context changes, so capturing `onScriptRun` in its
     * closure would pin whatever value existed at install time — `undefined`
     * before the provider mounts. A ref is read at call time instead, which is
     * the difference between the fast path working and silently never arming.
     */
    const onScriptRunRef = useRef(useRealtimeOptional()?.onScriptRun)
    onScriptRunRef.current = useRealtimeOptional()?.onScriptRun
    const [manifestVersion, setManifestVersion] = useState(0)
    // Bumped to RE-RUN the consent gate on demand (the block panel's "Review
    // permission"). Re-arming `consentPromptedRef` is necessary but not
    // sufficient — the gate lives in an effect, so something in its dep list must
    // change or it never re-reads the requirement and no dialog opens.
    const [consentCheckGen, setConsentCheckGen] = useState(0)
    const manifestRecheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => {
      if (!onFileEvent || accountsContext !== 'app' || !parentId) return
      const unsub = onFileEvent((event) => {
        if (event.parentId !== parentId && event.oldParentId !== parentId) return
        // #538 — only the manifest decides what the PDApp asks for. Re-asking on ANY write in its
        // folder (the PDApp's own data, an export) re-prompted a person who had just declined, and
        // restarted a check already in flight. An event without a name is re-checked (fail open
        // to asking the server, never to a stale answer).
        if (event.name && event.name.toLowerCase() !== 'manifest.json') return
        if (manifestRecheckTimer.current) clearTimeout(manifestRecheckTimer.current)
        manifestRecheckTimer.current = setTimeout(() => {
          consentPromptedRef.current = false // allow the gate to re-prompt
          setManifestVersion((v) => v + 1) // re-trigger the gate effect below
        }, 250)
      })
      return () => {
        unsub()
        if (manifestRecheckTimer.current) clearTimeout(manifestRecheckTimer.current)
      }
    }, [onFileEvent, accountsContext, parentId])

    // ── Proactive consent gate (before the app acts) ──────────────────────
    // For an app that DECLARES required identity (`wants_identity`), check the
    // requirement up-front and prompt BEFORE the app does anything — instead of
    // waiting for its first scripts.run/whoami to 412. Decline → the block panel
    // (the app never runs dead). Optional-only apps (no required field) load
    // normally — this is a no-op for them, and any fetch failure also just lets
    // the app load (the reactive scripts.run gate remains the backstop).
    useEffect(() => {
      // #538 — only for a window on screen: a kept-alive window in the background neither asks
      // the server nor takes the fence (which would hold the question away from a shown viewer).
      if (accountsContext !== 'app' || !parentId || !screenActive) return
      let cancelled = false
      // Whether THIS invocation is the one holding the fence, so the cleanup
      // below can release it. Needed because the `finally` around the dialog
      // only runs when the promise SETTLES, and an unmount never settles it —
      // the dialog is torn down without anyone calling `resolve`. Without this
      // release, closing an app mid-prompt strands the folder id for the life
      // of the document and NO viewer prompts for that app again: a silent,
      // page-wide failure worse than the duplicate prompt the fence prevents.
      void (async () => {
        type Requirement = {
          required?: string[]; optional?: string[]
          alreadyGranted?: string[] | null; alreadyDeclined?: string[] | null
          appName?: string; appOwnerName?: string | null
        }
        let req: Requirement | null = null
        try {
          // Shared with every other reader asking right now (fetchConsentRequirement) — a re-run
          // while the viewer settles reuses the first request instead of waiting for its own.
          const answer = await fetchConsentRequirement<Requirement>(consentRequirementUrl(parentId))
          if (!answer.ok) return // no principal / no manifest / forbidden → nothing to gate
          req = answer.body
        } catch { return }
        if (cancelled || !req) return
        const required = Array.isArray(req.required) ? req.required : []
        const optional = Array.isArray(req.optional) ? req.optional : []
        const granted = new Set(req.alreadyGranted ?? [])
        const declined = new Set(req.alreadyDeclined ?? [])
        // UNDECIDED = declared by the manifest but neither granted nor declined.
        // Prompts on FIRST load (no decision → everything undecided) AND when the
        // manifest adds a NEW field — but never re-nags a field already decided.
        const newFields = [...required, ...optional].filter((f) => !granted.has(f) && !declined.has(f))
        const missingRequired = required.filter((f) => !granted.has(f))
        if (newFields.length === 0 && missingRequired.length === 0) {
          // Nothing left to ask. Clear any block panel first: a viewer that lost
          // the fence shows one, and its own "Review permission" re-runs THIS
          // effect — which used to return here without clearing, so the button
          // did nothing and the panel outlived the grant another viewer had just
          // made. That is the symptom `retryConsent` exists to fix, re-created by
          // the fence's blocked branch.
          //
          // AND RELOAD, when a panel was actually up — where `triggerReload` can.
          // It no-ops without a `baseUrl`, which is inline/single-file mode
          // (`HtmlEditor` passes `baseUrl={singleFileMode ? null : …}`, and any
          // `previewSessionError` sets that), so there the panel still clears
          // over a frame nothing re-runs. Strictly better than before and not a
          // full fix: a session-less preview has no reload to give.
          //
          // Clearing alone removed the
          // explanation and left the app exactly as dead as before: its boot
          // `whoami`/`scripts.run` was answered `consent_declined` and does not
          // retry, so the user got a blank frame where a panel with a button used
          // to be — strictly worse than the state they clicked to escape. Guarded
          // on the previous value rather than fired unconditionally, so the
          // ordinary "already consented, nothing to do" pass on every mount and
          // every `manifestVersion` bump does not reload the app under the user.
          const hadPanel = !!consentBlockedRef.current
          setConsentBlocked(null)
          if (hadPanel) triggerReloadRef.current?.()
          return
        }
        if (consentPromptedRef.current) return
        // Another mounted viewer is already asking about THIS app — see
        // `consentGateOpen`. Returning leaves that one prompt to answer for
        // both, instead of a second dialog that outlives the write.
        // Owner-checked take. Release cannot delete a fence a different run
        // holds, and the owner lives in a ref that outlives this closure.
        const fenceToken = takeFence(parentId)
        if (!fenceToken) return
        consentPromptedRef.current = true
        const appName = req.appName ?? 'This PDApp'
        const params = {
          appFolderId: parentId, appName, appOwnerName: req.appOwnerName ?? null,
          required, optional, newFields,
          // Seed from the RECORD. Without these a delta prompt — the manifest
          // added a field after the user decided about the rest — defaulted
          // every optional field on, so pressing Allow silently re-granted one
          // they had declined. `newFields` keeps the footer a prompt's
          // (Decline / Allow) and is the only set that may default on.
          granted: req.alreadyGranted ?? undefined,
          declined: req.alreadyDeclined ?? undefined,
        }
        let decision: Awaited<ReturnType<typeof openConsentDialog>>
        try {
          // THE HOST DIALOG, always — not the embed. The fence makes exactly one
          // viewer responsible for asking, and the winner is decided by which
          // requirement fetch lands first. The embed renders INSIDE its viewer's
          // subtree, and a backgrounded app deck is `opacity-0
          // pointer-events-none` (kept mounted, not unmounted), so when a hidden
          // viewer won the race the prompt was mounted invisible and
          // click-through: no dialog anywhere, unanswerable, until a reload.
          // Before the fence both viewers opened one and a visible one always
          // existed, so this was a regression the fence introduced.
          //
          // The host dialog is portaled to `document.body` at z-index 9999, so
          // it is visible whichever viewer owns it. It is also the surface the
          // #525 redesign lives on, so prompts and Manage now look alike.
          decision = await openConsentDialog(params)
        } finally {
          // Covers a throw. An UNMOUNT never reaches here — the promise simply
          // never settles — which is what the cleanup's release is for.
          dropFence(parentId, fenceToken)
        }
        // NOT `if (cancelled) return`.
        //
        // Reaching here means the dialog RESOLVED, and only a rendered dialog
        // can resolve it — so the component is mounted and `cancelled` cannot
        // mean "unmounted". It can only mean a dependency changed while the user
        // was reading the dialog, and `manifestVersion` bumps on ANY file event
        // in the app folder (an agent write, a collaborator's save, an SSE
        // reload). The re-run then bails at `takeFence` because this run still
        // holds the fence, so nothing else applies the answer either: the user
        // pressed Allow, the grant landed server-side, and the bar never
        // refreshed, the app never reloaded, and — on a decline — the block panel
        // never appeared, leaving a dead page with no explanation.
        //
        // Before the fence a losing re-run opened its own dialog, so the flow
        // still completed. The fence closed that door; this line was the only
        // thing still walking through it.
        //
        // What genuinely must not be applied is an answer about a DIFFERENT app:
        // `parentId` changing means this viewer has moved on, and the decision
        // belongs to the folder it was asked about. That is the identity check,
        // and it is strictly narrower than `cancelled`.
        if (parentIdRef.current !== parentId) return
        // A real decision (allow OR decline) changed the grants → refresh the bar's mask.
        if (decision) refreshAccountsBarRef.current()
        // Block ONLY when the app REQUIRES a field still ungranted after the dialog.
        // Declining an OPTIONAL opt-in just loads the app with those fields null.
        const grantedNow = decision && decision.decision !== 'decline'
          ? new Set(decision.grantedFields || []) : new Set<string>()
        const requiredUnmet = required.filter((f) => !granted.has(f) && !grantedNow.has(f))
        setConsentBlocked(requiredUnmet.length > 0 ? { appName, required, reason: 'declined' } : null)
        // A grant that satisfies the required fields → RE-RUN the app so it re-reads
        // the now-consented identity. The app's load-time whoami()/scripts.run ran
        // BEFORE the grant (rejected / returned nulls) and does not retry on its own,
        // so without this the app keeps its pre-consent identity until a manual
        // refresh. The "Manage permissions" path (open-consent-page, below) already
        // reloads on a decision; the proactive first-load gate omitted it — which is
        // the reload the gate's own docblock says the caller must do. Skip on a pure
        // dismiss (no decision) or a decline (the block panel / null fields handle it).
        if (decision && decision.decision !== 'decline' && requiredUnmet.length === 0) {
          triggerReloadRef.current?.()
        }
      })()
      // The fence is NOT released here. This cleanup also runs on every
      // dependency change, and freeing it there would open the door while the
      // dialog it guards is still on screen. `fenceHeldRef`'s `[]`-deps cleanup
      // is the unmount release.
      return () => { cancelled = true }
    }, [accountsContext, parentId, manifestVersion, consentCheckGen, openConsentDialog, consentRequirementUrl, screenActive])

    // Per-instance warn-once diagnostic for the openExternal / openFile
    // cases — consumers of @papan/collab who forget to wire the props
    // shouldn't get a silent UX bug. See iframe-message-gate.useWarnOnce.
    const warnOnce = useWarnOnce()
    // Track SSE reload generation to avoid stale swaps
    const reloadGenRef = useRef(0)
    // Off-origin detection (session mode only). Each iframe `load` event
    // bumps that iframe's loadGen; if no matching `papan:nav` arrives
    // within 500ms, the iframe has navigated to a non-preview-origin URL
    // where our injected snapshot script isn't present. We can't read
    // the cross-origin URL from the parent (sandbox opaque-origin throws
    // SecurityError on `contentWindow.location.href`), so we surface a
    // sentinel `'about:external'` to indicate "iframe is somewhere we
    // can't see" — ShareViewerChrome renders this as "Now viewing:
    // External site". The 500ms grace handles the normal in-iframe
    // `window.load` → `parent.postMessage('papan:nav', …)` round-trip,
    // which is microseconds in practice.
    //
    // Per-iframe tracking (not a single shared gen) is required because
    // the SSE double-buffer reload fires loads on iframe1 while iframe0
    // might be in the middle of a separate navigation. A shared counter
    // lets iframe1's legitimate `papan:nav` mask a missing `papan:nav`
    // on iframe0 (false negative — laundering goes undetected).
    const loadGenRef = useRef<{ a: number; b: number }>({ a: 0, b: 0 })
    const lastNavGenRef = useRef<{ a: number; b: number }>({ a: -1, b: -1 })
    // Mount sentinel used to swallow setTimeout callbacks that fire
    // after unmount. Avoids `setState on unmounted component` warnings
    // and stops a stale `about:external` from leaking into a newly
    // mounted ShareViewerChrome instance.
    const mountedRef = useRef(true)
    /** #796 — the server answered `reentry_required` (the app REQUIRES identity and this visit
     *  has none): put up the panel's SIGN-IN (never the consent dialog — consent needs an
     *  account). Checks the body and any per-op results (db.batch). */
    const offerSignInOnReentry = (body: unknown, results: unknown[] = []) => {
      const hit = [body, ...results].find((r): r is Record<string, unknown> =>
        !!r && typeof r === 'object' && (r as { reentry_required?: unknown }).reentry_required === true)
      if (!hit || !mountedRef.current) return
      setConsentBlocked({
        appName: typeof hit.appName === 'string' ? hit.appName : 'This PDApp',
        required: Array.isArray(hit.required) ? hit.required as string[] : [],
        reason: 'reentry',
      })
    }

    const isSessionMode = !!baseUrl
    // The block panel's glass reads the theme from its own class (it may render outside a ThemedArea).
    // Optional: the viewer also renders where no ThemeProvider is mounted (tests, embedded hosts) —
    // the panel then takes its enclosing area's theme.
    const themeCtx = useThemeOptional()
    const blockThemeClass = themeCtx ? getThemeClass(themeCtx.settings.theme.workspace) : ''

    useImperativeHandle(ref, () => ({
      get iframe() {
        if (isSessionMode) {
          return activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current
        }
        return iframe0Ref.current
      },
      reload(): void {
        // Via the ref so this doesn't need triggerReload in its dep array
        // (triggerReload is defined below and mirrored into triggerReloadRef).
        triggerReloadRef.current?.()
      },
      requestSnapshot(): Promise<string | null> {
        // Works in BOTH modes now: session mode snapshots the active
        // double-buffer iframe; inline mode snapshots the single srcDoc
        // iframe (iframe0Ref), which renders scripts live and carries the
        // same injected bridge. Capturing the live render is what keeps the
        // PDF an exact snapshot of what the user sees.
        //
        // Fast-path: inline mode showing an EXTERNAL page (`src` set via
        // link navigation) carries no injected bridge, so a snapshot would
        // never be answered. Return null immediately for an instant fallback
        // to source content instead of blocking on the 15s timeout below.
        if (!isSessionMode && src) return Promise.resolve(null)
        const active = isSessionMode
          ? (activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current)
          : iframe0Ref.current
        if (!active?.contentWindow) return Promise.resolve(null)

        return new Promise((resolve) => {
          const reqId = `snap_${Date.now()}`
          // Source-gated via addScopedMessageListener against the active
          // iframe's contentWindow — an attacker window with a handle to
          // ours can guess the timestamp-based reqId, so reqId alone is
          // not sufficient.
          const removeListener = addScopedMessageListener(active!.contentWindow, (e) => {
            if (frameAppOriginRef.current && e.origin !== frameAppOriginRef.current) return
            if (e.data?.type === 'papan:snapshot:result' && e.data.reqId === reqId) {
              cleanup()
              resolve(e.data.error ? null : e.data.html)
            }
          })
          const timeout = setTimeout(() => { cleanup(); resolve(null) }, 15000)
          function cleanup() {
            clearTimeout(timeout)
            removeListener()
          }
          active!.contentWindow!.postMessage({ type: 'papan:snapshot', reqId }, frameTargetOrigin())
        })
      },
      postDesignCommand(msg: { type: string; [k: string]: unknown }): void {
        const active = isSessionMode
          ? (activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current)
          : iframe0Ref.current
        try { active?.contentWindow?.postMessage(msg, frameTargetOrigin()) } catch { /* iframe gone */ }
      },
      markContentRendered(c: string): void {
        renderedContentRef.current = c
        lastContentRef.current = c
        // Book the already-applied change into the iframe's patch baseline
        // so subsequent typing diffs stay single-region and its journal
        // keeps mapping serve-time locs correctly. Fire-and-forget.
        const active = isSessionMode
          ? (activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current)
          : iframe0Ref.current
        try {
          active?.contentWindow?.postMessage(
            { type: 'papan:preview-sync', source: c, applied: true, reqId: `ap${++syncSeqRef.current}` },
            frameTargetOrigin(),
          )
        } catch { /* iframe gone */ }
      },
      expediteNextReload(): void {
        expediteReloadRef.current = true
      },
      flushPendingPatch(): Promise<boolean> {
        if (contentRef.current === renderedContentRef.current) return Promise.resolve(true)
        return new Promise((resolve) => {
          patchWaitersRef.current.push(resolve)
          attemptPatchOrReloadRef.current()
        })
      },
    }), [isSessionMode, activeIdx, src])

    // ─── Design mode: arm/disarm the injected inspector ────────────────
    // The inspector script (injected server-side for user-kind preview
    // sessions) is inert until it receives `papan:design-mode`. Post on
    // toggle + buffer swap; the `papan:nav` handler below re-arms after
    // every page load (SSE reload, typing reload, in-app navigation) since
    // a fresh document starts inert again.
    useEffect(() => {
      const active = isSessionMode
        ? (activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current)
        : iframe0Ref.current
      try {
        active?.contentWindow?.postMessage(
          { type: 'papan:design-mode', on: designMode, editableFile: designEditableFile ?? null },
          frameTargetOrigin(),
        )
        // Arm save-hotkey capture when the embedder is an editable editor.
        // Re-armed here on every load (activeIdx flips on the reload swap)
        // since a fresh document starts with the flag off. The agent eval
        // bridge (preview_eval / app-inspect) rides the SAME editable-editor
        // gate: `onSaveHotkey` is undefined for read-only / version previews
        // (HtmlEditor), so eval stays off on non-editable + public previews.
        if (onSaveHotkeyRef.current) {
          active?.contentWindow?.postMessage({ type: 'papan:enable-save-hotkey' }, frameTargetOrigin())
          active?.contentWindow?.postMessage({ type: 'papan:enable-eval' }, frameTargetOrigin())
        }
      } catch { /* iframe gone */ }
    }, [designMode, designEditableFile, isSessionMode, activeIdx])

    // ─── Inline mode: build srcDoc ─────────────────────────────────────

    const initialScrollTop = viewSync && viewSyncId
      ? (viewSync[viewSyncId]?.scrollTop as number | undefined)
      : undefined
    // Inject the SAME bridge the server injects for session previews, so the
    // inline (Single File Mode / version preview) iframe is snapshot-capable.
    // requestSnapshot() can then capture the live, fully-JS-rendered DOM for
    // PDF export instead of re-rendering raw script-free content.
    // Inline mode: inject the snapshot bridge at end-of-body, and — when this
    // preview has a debug-log target (author/editor only) — the console-capture
    // shim EARLY in <head> so it installs before the app's initial run and
    // catches load-time errors. Session mode injects the shim server-side, also
    // in <head> (preview-serve, same author/editor gate).
    // evalBridge rides the editable-editor signal (onSaveHotkey present) — the
    // same gate that later SENDS papan:enable-eval — so read-only / version
    // previews don't even carry the preview_eval code.
    const withSnapshot = injectScript(content, getSnapshotScript({ initialScrollTop, evalBridge: !!onSaveHotkey }))
    // injectAtHeadStart is shared with preview-serve; on a head-less page it
    // lands after <html>/the doctype, never before it (that forces quirks mode).
    const withCapture = debugLogTarget ? injectAtHeadStart(withSnapshot, getConsoleCaptureScript()) : withSnapshot
    // The storage shim (#312) goes in LAST so it lands FIRST in <head>: this
    // iframe is opaque-origin (no allow-same-origin), where localStorage /
    // sessionStorage / document.cookie throw, so it must install before any
    // page script. Session mode gets the same shim server-side (preview-serve).
    const srcDoc = injectAtHeadStart(withCapture, getSandboxStorageShimScript())

    // ─── Session mode: double-buffer reload function ─────────────────

    const disposedRef = useRef(false)

    // Tracks the source `content` that the currently-displayed iframe was
    // loaded against. Used to skip spurious reloads (e.g. a collab reconnect
    // fires the `/__reload` SSE even though the HTML is byte-identical) — a
    // reload throws away in-iframe state like filled-in forms, so we only do
    // it when the code actually changed. `contentRef` mirrors the live prop
    // so triggerReload can read it without churning its identity (which would
    // re-run the SSE effect and itself reload the iframe).
    const contentRef = useRef(content)
    contentRef.current = content
    const renderedContentRef = useRef<string | null>(null)
    // Mirror the scroll-sync state so triggerReload's onload can replay the
    // last-known scroll after a reload (e.g. a structural reorder) without
    // re-creating the callback (which would re-run the SSE effect).
    const viewSyncStateRef = useRef({ viewSync, viewSyncId })
    viewSyncStateRef.current = { viewSync, viewSyncId }

    const triggerReload = useCallback(() => {
      if (!baseUrl || disposedRef.current) return
      // Capture the content we're about to load; record it as "rendered" only
      // AFTER the swap actually happens (in onload below). Setting it up-front
      // would mark content as rendered even if this load is superseded by a
      // newer reload or never fires onload — then a later identical-content SSE
      // would be wrongly skipped, leaving a stale iframe.
      const loadedContent = contentRef.current
      // Re-arm consent for the fresh load: drop the block panel and let the
      // dialog prompt once more if required consent is still unmet.
      consentPromptedRef.current = false
      setConsentBlocked(null)
      // The reload re-mints the ws-token and reconnects; clear the cap banner and
      // let the fresh SDK re-raise `papan:realtime-unavailable` if it's STILL over
      // the cap (avoids a stale banner after the user closes apps + reloads).
      setRealtimeCapped(false)
      setNeedsAppOrigin(null)
      setAppLinkError(null)
      reloadGenRef.current++
      const gen = reloadGenRef.current
      setPendingLoad(true)
      // #356: tell the agent's preview_eval / preview_screenshot to DEFER their
      // live dispatch while this reload is in flight (they retry via /pending
      // until it settles) instead of fast-failing "preview not open" mid-swap.
      // A self-expiring epoch-ms deadline (window contract read by
      // isPreviewReloading() in @papan/agent — no import, avoids a dep cycle); the
      // bound guarantees a reload that never fires onload can't defer the tools
      // forever. Cleared on swap onload below.
      try { (window as unknown as Record<string, unknown>).__papanPreviewReloadingUntil = Date.now() + 15_000 } catch { /* SSR */ }

      setActiveIdx(prev => {
        const bgIdx = prev === 0 ? 1 : 0
        const bgIframe = bgIdx === 0 ? iframe0Ref.current : iframe1Ref.current
        if (bgIframe) {
          const entry = entryFile || 'index.html'
          // A new page: whether it runs the SDK is its own handshake's to say.
          if (bgIframe.contentWindow) sdkWindowsRef.current.delete(bgIframe.contentWindow)
          bgIframe.src = `${baseUrl}/${encodeServePath(entry)}${withReloadStamp(entrySuffix)}`
          bgIframe.onload = () => {
            if (disposedRef.current || reloadGenRef.current !== gen) return
            renderedContentRef.current = loadedContent
            // Seed the patch engine's baseline with the EXACT content this
            // load was served from (the nav-handler baseline can race the
            // renderedContent update; this one can't).
            try {
              bgIframe.contentWindow?.postMessage(
                { type: 'papan:preview-baseline', source: loadedContent, file: entry },
                frameTargetOrigin(),
              )
            } catch { /* iframe gone */ }
            // Restore scroll: the fresh iframe loads at the top, but the
            // bridge has been reporting scroll via papan:vs, so replay the
            // last-known position through the same papan:scrollTo path the
            // close re-sync uses. Keeps your place through a reorder reload.
            try {
              const vs = viewSyncStateRef.current
              const st = vs.viewSync && vs.viewSyncId ? (vs.viewSync[vs.viewSyncId]?.scrollTop as number | undefined) : undefined
              if (typeof st === 'number' && st > 0) {
                const sl = (vs.viewSync?.[vs.viewSyncId!]?.scrollLeft as number | undefined) ?? 0
                bgIframe.contentWindow?.postMessage({ type: 'papan:scrollTo', st, sl }, frameTargetOrigin())
              }
            } catch { /* iframe gone */ }
            setActiveIdx(bgIdx as 0 | 1)
            setPendingLoad(false)
            // #356: reload settled — let the preview inspection tools dispatch again.
            try { (window as unknown as Record<string, unknown>).__papanPreviewReloadingUntil = 0 } catch { /* SSR */ }
          }
        } else {
          // #356: no background buffer to reload into — no swap onload will fire,
          // so nothing would clear the flag we armed above. Disarm it now, else
          // the inspection tools defer needlessly until the 15s self-expiry.
          try { (window as unknown as Record<string, unknown>).__papanPreviewReloadingUntil = 0 } catch { /* SSR */ }
        }
        return prev
      })
    }, [baseUrl, entryFile, entrySuffix])

    // Block panel "Review permission" → re-run the consent gate, and LEAVE THE
    // PANEL UP while the dialog is open.
    //
    // This used to call `triggerReload()` alone, on the belief that a reload
    // "re-arms the consent dialog". It re-armed the FLAG (`consentPromptedRef`)
    // and cleared the panel — so the panel vanished and nothing else happened,
    // which is exactly how it was reported: "clicking it doesn't show the
    // permission prompt, it just hides the dialog". The gate is an effect, and a
    // reload changes none of its deps (`reloadGenRef` is a ref; `pendingLoad`
    // isn't in the list), so it never re-read the requirement.
    //
    // Bumping `consentCheckGen` is what actually re-runs it.
    //
    // It deliberately does NOT clear the panel or reload:
    //  - clearing it left a blank frame behind the dialog, and nothing to return
    //    to if the user dismissed the prompt a second time;
    //  - the dialog renders at z-index 2147483646 against the panel's 50, so it
    //    covers it regardless;
    //  - the panel is updated by the gate's own `setConsentBlocked` once a
    //    decision lands — cleared on a grant, kept on another decline;
    //  - and the RELOAD already has an owner: the embed posts
    //    `papan:accounts:consent-changed` when grants change, which
    //    `triggerReloadRef` handles. Reloading here as well raced that path and
    //    reloaded even when nothing had been granted.
    //
    // AMENDED, twice. The gate's "nothing left to ask" path DOES now clear the
    // panel and reload — but only when a panel was actually up (`hadPanel`),
    // which is the case this docblock's second bullet does not cover: another
    // viewer answered, so no decision lands in THIS one and nothing else ever
    // clears it. Clearing without reloading left a blank frame, which is the
    // outcome the first bullet warns about; the two together are the fix.
    //
    // And that path runs ONLY under `accountsContext === 'app'` — the gate
    // effect returns on its first line otherwise — so the reload above is
    // conditional rather than owned entirely by the effect. Under `'app'` the
    // ownership argument stands unchanged.
    const retryConsent = useCallback(() => {
      consentPromptedRef.current = false
      setConsentCheckGen((g) => g + 1)
      // …and in the SHARE view, reload, because the generation bump has no
      // reader there. The proactive gate is the only consumer of
      // `consentCheckGen` and its first line is
      // `if (accountsContext !== 'app' || !parentId) return`, so on a shared
      // link "Review permission" reset a flag and bumped a counter nobody
      // watched: no dialog, no reload, no change to the panel, forever. The
      // recipient's only working route was the accounts bar, which the panel
      // does not mention.
      //
      // A reload re-runs the app, whose `scripts.run` 412s again — and with the
      // flag just cleared and the fence free, THAT is what opens the dialog. So
      // the reactive gate does here exactly what the proactive one does under
      // `'app'`. Not done in both contexts: under `'app'` the effect handles it
      // and reloading as well would race the embed's `consent-changed` path,
      // which is what the note above is about.
      //
      // It does clear the panel on the way (`triggerReload` → `setConsentBlocked
      // (null)`), which the first bullet above argues against — and that
      // argument was about clearing WITHOUT a reload. Here the reload is what
      // brings the dialog back, so the panel is replaced rather than removed.
      // The residual is an app that does not re-issue the 412 on load (an
      // expired preview session, or one that only calls `scripts.run` on user
      // action): that user is left with neither panel nor dialog. Strictly
      // better than a button that did nothing, and the same shape the `'app'`
      // path already accepts.
      if (accountsContext !== 'app') triggerReloadRef.current?.()
    }, [accountsContext])

    // Stable ref so the (stable) message-handler map can reload after the
    // SDK bar relays a consent reset.
    const triggerReloadRef = useRef(triggerReload)
    triggerReloadRef.current = triggerReload
    const openManageConsentRef = useRef(openManageConsent)
    openManageConsentRef.current = openManageConsent
    /** After Manage permissions: nothing on a dismiss; a decision that leaves a required field
     *  ungranted shows the block panel (no reload — the reload would clear it); any other decision
     *  reloads the app so it reads its new identity. Either way the bar re-reads what it may show. */
    const manageConsent = useCallback(async () => {
      const decision = await openManageConsentRef.current()
      if (!decision) return
      refreshAccountsBarRef.current()
      if ('blocked' in decision && decision.blocked) {
        consentPromptedRef.current = true
        setConsentBlocked({ appName: decision.blocked.appName, required: decision.blocked.required, reason: 'declined' })
      } else {
        setConsentBlocked(null)
        triggerReloadRef.current()
      }
    }, [])
    const manageConsentRef = useRef(manageConsent)
    manageConsentRef.current = manageConsent

    // Listen for the Accounts embed's layout/consent signals (trusted: it's our
    // own same-origin embed iframe; verified by e.source).
    useEffect(() => {
      const onMsg = (e: MessageEvent) => {
        // (D6) The embed is served from this SPA's own origin, so a genuine signal
        // is same-origin AND from the embed's window. Both gates, belt-and-suspenders.
        if (e.source !== embedRef.current?.contentWindow || e.origin !== window.location.origin) return
        const d = e.data as { source?: string; type?: string; mode?: string; requestId?: string; decision?: string; grantedFields?: string[] } | null
        if (!d || d.source !== 'papan-accounts') return
        if (d.type === 'papan:accounts:resize') setEmbedExpanded(d.mode === 'overlay')
        else if (d.type === 'papan:accounts:close') setEmbedHidden(true)
        // #538 (trial): the capsule's Share and star — the window's own Share dialog and Pin.
        else if (d.type === 'papan:accounts:size') {
          const w = Number((d as { w?: unknown }).w), h = Number((d as { h?: unknown }).h)
          if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) setBarBox({ w: Math.min(w, 2000), h: Math.min(h, 120) })
        }
        else if (d.type === 'papan:accounts:share') barActionsRef.current?.onShare?.()
        else if (d.type === 'papan:accounts:favorite') barActionsRef.current?.onTogglePin?.()
        // NOTE: the embed no longer emits `switch-account`/`signed-out` (#187) — the
        // share-context bar is display-only and account actions moved to the native
        // top-level chrome. The handlers that consumed them are removed.
        else if (d.type === 'papan:accounts:consent-changed') triggerReloadRef.current()
        // R13 — bar delegates "Manage permissions" here so the consent dialog renders in
        // the host's top-level DOM (un-clickjackable), not inside the frameable embed.
        // Reload the app on a real decision (matches the old in-frame flow's behavior).
        else if (d.type === 'papan:accounts:open-consent-page') void manageConsent()
      }
      window.addEventListener('message', onMsg)
      return () => window.removeEventListener('message', onMsg)
    }, [])

    // ─── Session mode: SSE for external changes (file save, other users) ──

    useEffect(() => {
      if (!baseUrl) return
      disposedRef.current = false

      let es: EventSource | null = null
      let reconnectDelay = 1000
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null

      const connect = () => {
        if (disposedRef.current) return
        // raw (#1316): the preview host's own change stream, keyed on `baseUrl` (a new session = a new base = a new
        // stream) — it carries no answer to drop, only "reload what is on screen"
        es = new EventSource(`${baseUrl}/__reload`)
        es.onopen = () => { reconnectDelay = 1000 }
        // Gate on the editor's "Auto-refresh" toggle. Stay subscribed so the
        // connection survives toggling, but only reload when enabled.
        //
        // Skip the reload ONLY when the change is to THIS preview's entry file
        // AND its rendered content is byte-identical — i.e. a collab reconnect
        // (epoch change → no-op resave) that would otherwise wipe in-iframe
        // state (filled forms, scroll). The server tags the SSE with the
        // changed file id; a sibling asset (different id) or a multi-file batch
        // ('reload') always reloads, so multi-file live-reload is preserved.
        es.onmessage = (e: MessageEvent) => {
          if (!autoRefreshRef.current) return
          const changedId = typeof e.data === 'string' ? e.data : ''
          // Match on the source-resolved id so cross-tenant .shared / .chain
          // previews work too: the event and previewFileId may carry different
          // representations of the same file (plain source id vs a
          // s_<mount>_<src> compound, or different nesting). mountSourceFileId
          // normalizes both to the innermost source id. ('reload' for a
          // multi-file batch resolves to itself → never matches → reload.)
          const isEntryFile = !!previewFileId && mountSourceFileId(changedId) === mountSourceFileId(previewFileId)
          if (isEntryFile && renderedContentRef.current !== null && contentRef.current === renderedContentRef.current) return
          triggerReload()
        }
        es.onerror = () => {
          es?.close()
          es = null
          if (disposedRef.current) return
          reconnectTimer = setTimeout(() => {
            reconnectDelay = Math.min(reconnectDelay * 2, 30000)
            connect()
          }, reconnectDelay)
        }
      }

      // Initial load of the foreground iframe
      const entry = entryFile || 'index.html'
      const fgIframe = iframe0Ref.current
      if (fgIframe) {
          const initialContent = contentRef.current
          if (fgIframe.contentWindow) sdkWindowsRef.current.delete(fgIframe.contentWindow)
          fgIframe.src = `${baseUrl}/${encodeServePath(entry)}${normalizeEntrySuffix(entrySuffix)}`
          renderedContentRef.current = initialContent  // baseline for the SSE skip-if-unchanged check
          // Seed the patch engine's baseline once the document exists —
          // with the EXACT content this load serves. (React's onLoad prop
          // and this .onload property are independent; both fire.)
          fgIframe.onload = () => {
            try {
              fgIframe.contentWindow?.postMessage(
                { type: 'papan:preview-baseline', source: initialContent, file: entry },
                frameTargetOrigin(),
              )
            } catch { /* iframe gone */ }
          }
      }
      setActiveIdx(0)

      connect()

      return () => {
        disposedRef.current = true
        es?.close()
        if (reconnectTimer) clearTimeout(reconnectTimer)
        // #356: if a reload was mid-flight, its swap onload bails on `disposedRef`
        // BEFORE clearing the reload flag — so unmounting the viewer would leave
        // `__papanPreviewReloadingUntil` armed (up to 15s), during which a
        // preview_eval / preview_screenshot against a genuinely-closed preview
        // would DEFER instead of returning the actionable "not open" message.
        // Clear it here so teardown never strands the flag.
        try { (window as unknown as Record<string, unknown>).__papanPreviewReloadingUntil = 0 } catch { /* SSR */ }
      }
    }, [baseUrl, entryFile, entrySuffix, triggerReload])

    // ─── Session mode: patch-or-reload on local content changes ───────
    // Debounced at 1.5s of idle time — full page reloads are expensive
    // (scripts re-execute, Chart.js replays animations, etc.). Before
    // reloading, offer the change to the in-iframe patch engine: if the
    // settled diff is a pure text or start-tag-attribute change on ONE
    // element (verified against the live DOM), it's applied in place and
    // the reload is skipped entirely. Any refusal, mismatch, or timeout
    // (engine absent / mid-load) falls back to the reload — fail-safe.

    // flushPendingPatch waiters — resolved with `true` when a patch settled
    // the preview in place, `false` when we fell back to a reload.
    const patchWaitersRef = useRef<Array<(ok: boolean) => void>>([])
    const resolvePatchWaiters = (ok: boolean) => {
      const waiters = patchWaitersRef.current
      patchWaitersRef.current = []
      for (const w of waiters) w(ok)
    }

    // Deferred reload after a patch refusal. Patch attempts run FAST (250ms
    // after a content change — a remote design commit should appear almost
    // immediately), but an unpatchable change (structural typing in the
    // Source pane) must still COALESCE into one reload — so the reload
    // fires only if no further change supersedes it. Every new attempt
    // cancels the pending reload first.
    // Set by expediteNextReload; consumed by the next patch attempt.
    const expediteReloadRef = useRef(false)
    const pendingReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scheduleReloadSoon = useCallback(() => {
      if (pendingReloadRef.current) clearTimeout(pendingReloadRef.current)
      if (expediteReloadRef.current) {
        // A discrete undo/redo — nothing is coming that this could merge with.
        expediteReloadRef.current = false
        triggerReload()
        return
      }
      pendingReloadRef.current = setTimeout(() => {
        pendingReloadRef.current = null
        triggerReload()
      }, 1250)
    }, [triggerReload])
    useEffect(() => () => { if (pendingReloadRef.current) clearTimeout(pendingReloadRef.current) }, [])

    const attemptPatchOrReload = useCallback(() => {
      if (pendingReloadRef.current) { clearTimeout(pendingReloadRef.current); pendingReloadRef.current = null }
      // Every exit below concludes THIS attempt, so the one-shot expedite is
      // spent here rather than at each return. Left standing it would survive
      // to a later, unrelated refusal and reload that one without coalescing.
      const expedite = expediteReloadRef.current
      expediteReloadRef.current = false
      if (!baseUrl || disposedRef.current) { resolvePatchWaiters(false); return }
      const newContent = contentRef.current
      if (renderedContentRef.current === newContent) { resolvePatchWaiters(true); return }
      const active = activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current
      const target = active?.contentWindow
      if (!target || renderedContentRef.current === null) { resolvePatchWaiters(false); triggerReload(); return }
      // Re-arm for the refusal paths below, which run after this call returns.
      expediteReloadRef.current = expedite
      const reqId = `ps${++syncSeqRef.current}`
      let settled = false
      const removeListener = addScopedMessageListener(target, (e) => {
        if (frameAppOriginRef.current && e.origin !== frameAppOriginRef.current) return
        if (e.data?.type !== 'papan:preview-sync:result' || e.data.reqId !== reqId) return
        settled = true
        cleanup()
        if (e.data.ok === true) {
          expediteReloadRef.current = false
          renderedContentRef.current = newContent
          lastContentRef.current = newContent
          const en = e.data.entry as { pos?: unknown; delta?: unknown } | null
          if (en && typeof en.pos === 'number' && typeof en.delta === 'number' && Number.isFinite(en.pos) && Number.isFinite(en.delta)) {
            onPreviewPatchedRef.current?.({ pos: en.pos, delta: en.delta })
          }
          resolvePatchWaiters(true)
        } else {
          // eslint-disable-next-line no-console
          console.debug('[preview-patch] refused → reload:', e.data.reason ?? '(no reason)')
          resolvePatchWaiters(false)
          scheduleReloadSoon()
        }
      })
      const timeout = setTimeout(() => { if (!settled) { cleanup(); resolvePatchWaiters(false); scheduleReloadSoon() } }, 1000)
      function cleanup() { clearTimeout(timeout); removeListener() }
      try {
        target.postMessage({ type: 'papan:preview-sync', source: newContent, reqId }, frameTargetOrigin())
      } catch {
        cleanup()
        resolvePatchWaiters(false)
        scheduleReloadSoon()
      }
    }, [baseUrl, activeIdx, triggerReload, scheduleReloadSoon])
    const attemptPatchOrReloadRef = useRef(attemptPatchOrReload)
    attemptPatchOrReloadRef.current = attemptPatchOrReload

    const contentGenRef = useRef(0)
    const lastContentRef = useRef('')
    useEffect(() => {
      if (!baseUrl) return
      // Skip the first render (initial load already handled above)
      if (contentGenRef.current === 0) { contentGenRef.current = 1; lastContentRef.current = content; return }
      if (content === lastContentRef.current) return
      lastContentRef.current = content
      // 250ms: patch attempts are cheap, so remote design commits show up
      // near-instantly. The expensive path (reload) is separately deferred
      // via scheduleReloadSoon when a patch is refused.
      const timer = setTimeout(() => attemptPatchOrReloadRef.current(), 250)
      // The cleanup does NOT spend the one-shot. It runs on the very render
      // the undo's content change causes — before the new timer is even set —
      // so clearing here meant the flag was always false by the time the
      // attempt read it, and the expedited reload never once happened. The
      // leak it was guarding against is already closed at the source: the
      // flag is armed only when an undo actually moved something.
      return () => clearTimeout(timer)
    }, [content, baseUrl])

    // ─── postMessage listener (both modes) ─────────────────────────────
    //
    // Routed through `useGatedMessageDispatcher` so the source gate (which
    // iframes own this listener's privileges) and the per-type origin gate
    // (bridge messages add an origin requirement on top) are one
    // declarative wiring instead of inline switch-statement gating. The
    // central abstraction is what review pass #4 was missing: per-call
    // listeners that diverged from this main one ended up unaudited.

    // C.1 / C.6 (2026-06-16): the `papan:handshake` / `papan:run` /
    // `papan:ws-token-request` SDK bridge is reintroduced here. Trust
    // model: source-gated only (the iframes' contentWindow). Origin is
    // 'null' under the sandbox-without-allow-same-origin attribute, so
    // we can't and don't try to match it — source identity (this is
    // OUR iframe element, we mounted it with our URL) is sufficient
    // because attacker HTML inside the same iframe can already do
    // everything our SDK can; the SDK's authority IS the iframe's
    // authority. The actual security boundary is the PARENT-SIDE
    // forwarding: we call /api/v1/scripts/run with the parent's session
    // cookie, scoped to the supplied folderId (which is the parent's,
    // not the iframe's claim).
    useGatedMessageDispatcher({
      acceptSource: acceptIframeRefs(iframe0Ref, iframe1Ref),
      requireOrigin: () => frameAppOriginRef.current,
      handlers: {
        'papan:handshake': (e) => {
          const src = e.source as Window | null
          if (!src) return
          sdkWindowsRef.current.add(src)
          try {
            src.postMessage(
              {
                type: 'papan:handshake-ack',
                runtime: 'papan',
                version: 1,
                folderId: parentIdRef.current ?? null,
              },
              frameTargetOrigin(),
            )
          } catch { /* iframe gone */ }
        },
        // Debug log — batches of captured console/error/network entries from the
        // injected capture shim. Source-gated (this is our iframe); we buffer +
        // coalesce and POST to the debug-log endpoint (no-op when no target). No
        // reply — fire-and-forget, no oracle back to the iframe.
        'papan:console': (e) => {
          if (!debugLogTargetRef.current) return
          const entries = (e.data as { entries?: unknown }).entries
          if (Array.isArray(entries)) enqueueDebugLog(entries)
        },
        'papan:run': async (e) => {
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const scriptPath = typeof e.data.scriptPath === 'string' ? e.data.scriptPath : null
          if (!scriptPath) {
            try { src.postMessage({ type: 'papan:run:response', requestId, ok: false, error: 'invalid_args' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          const folderId = parentIdRef.current
          if (!folderId) {
            try { src.postMessage({ type: 'papan:run:response', requestId, ok: false, error: 'no_folder' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          // #1316: the call belongs to the session it was asked in — nothing after that session ended goes out (a status
          // poll, a consent re-run would be sent as the NEXT session)
          const start = currentSessionSignal()
          const runOnce = async (): Promise<Record<string, unknown>> => {
            const res = await sessionFetch(`${getApiBase()}/api/v1/scripts/run`, {
              method: 'POST',
              credentials: 'include',
              // Forward the active tenant/org-email (consistency with ws-token +
              // whoami; scripts.run already resolves via the folder's tenant).
              headers: {
                'Content-Type': 'application/json',
                ...getDefaultHeaders(),
                // #471 — opt in to the 202. THIS host is the transport most
                // PDApps use and it ships with the app, so every Mode A / Mode B
                // app gets the async path with no app change and no risk of a
                // pinned old bundle: only the npm SDK on an external page can be
                // stale.
                [ASYNC_RUNS_HEADER]: ASYNC_RUNS_VALUE,
              },
              // `runId` is the idempotency handle (#471 D7) — fresh PER CALL, so
              // a retried enqueue attaches to the existing job instead of
              // starting a second run.
              body: JSON.stringify({ scriptPath, args: e.data.args, requestedFolderId: folderId, runId: mintRunId() }),
            })
            // #471 — iframe mode is the transport MOST PDApps use, and it had the
            // same collapse the SDK's ppn.gg twin did: a Cloudflare 524 at the ~100s
            // ceiling became `invalid_response`, indistinguishable from "the script
            // failed", and completed runs got re-triggered. One shared predicate with
            // the SDK (@papan/sdk/transport-error) so the two cannot drift.
            const body = await readInSession<Record<string, unknown>>(res, start, () => transportErrorBody(res.status))   // #1316 review round 1
            // The run outlived the server's bounded wait — follow it. The 202 is
            // NOT an outcome and must never reach the iframe: the shim resolves
            // `papan.scripts.run()` with whatever we post back, and handing an
            // app `{queued:true}` would make every long run look like a
            // malformed answer. Consent is unaffected — a 412 is raised at
            // RESOLVE time, before anything is queued, so it still arrives here
            // as a plain 4xx and the retry dance below is untouched.
            if (res.status === 202 && isQueuedRun(body)) {
              // #471 — the socket the SPA already holds carries the completion,
              // so the poll drops to a backstop. `onScriptRunRef` is read at CALL
              // time, not captured: this handler is installed once and the
              // realtime context can arrive (or reconnect) after it.
              return (await pollRun(body.jobId, fetchRunStatus, { notifier: makeRunNotifier() })).body
            }
            return body
          }
          /**
           * Wake the poll when the WebSocket says this run is done.
           *
           * Returns `undefined` when there is no realtime context — outside a
           * `RealtimeProvider` the poll keeps its original fast cadence, which
           * is the correct fallback rather than a slow poll with no push to
           * lead it.
           */
          const makeRunNotifier = () => {
            const subscribe = onScriptRunRef.current
            if (!subscribe) return undefined
            return (jobId: string, onDone: () => void) =>
              subscribe((e) => { if (e.jobId === jobId) onDone() })
          }
          /**
           * One status page for a detached run, or `null` when the poll was refused.
           *
           * A DROPPED poll (offline, a blip) is reported as `running` rather than
           * `null`: `null` ends the poll and reports a lost result, which for a
           * script that is still running fine is the exact false failure #471
           * exists to remove.
           *
           * A 429 or a 5xx is the same kind of bad moment and gets the same
           * answer — `isTerminalPollStatus` is shared with the npm SDK's copy so
           * the two cannot classify one differently, the same reason
           * `transportErrorBody` is shared rather than duplicated.
           */
          const fetchRunStatus = async (jobId: string): Promise<RunStatusBody | null> => {
            // #1316: once the session that asked ended, the poll ends — with a terminal answer (`pollRun` stops only on
            // `null` or a terminal state, and `null` would read as a lost result): the app hears `session_ended`
            if (sessionEndedSince(start)) return SESSION_ENDED_RUN_STATUS
            try {
              const res = await sessionFetch(`${getApiBase()}/api/v1/scripts/run-status/${encodeURIComponent(jobId)}`, {
                credentials: 'include',
                headers: { ...getDefaultHeaders() },
              })
              if (!res.ok) return isTerminalPollStatus(res.status) ? null : { state: 'running' }
              return await readInSession<RunStatusBody>(res, start)   // #1316 review round 1
            } catch (err) {
              if (sessionEndedSince(start, err)) return SESSION_ENDED_RUN_STATUS
              return { state: 'running' }
            }
          }
          try {
            let body = await runOnce()
            // R21 #140 — Parent owns the 412 → consent-dialog → retry
            // dance. The shim previously called `requestConsent` to ask
            // us to open the dialog; that path is gone because the iframe
            // can postMessage anything, including spoofed
            // `appName` / `appOwnerName` strings the dialog would render
            // as trusted chrome. The 412 response body carries the
            // server-authoritative manifest fields, so we open the
            // dialog with those and never read display strings from any
            // iframe message.
            // The server returns 412 (decision needed) or 403 (previously
            // declined) when the manifest requires identity the user hasn't
            // granted; both carry the dialog payload. Required consent is a
            // precondition to running, so we (re-)open the dialog ONCE per page
            // load — a refresh re-arms it (triggerReload / handleInlineLoad).
            if (body && body.ok === false && body.reentry_required === true) {
              // #796 — the app REQUIRES identity and this visit has none: a sign-in, not a
              // consent (the dialog could only fail — consent needs an account). The
              // panel's button hands off to the share viewer's own sign-in.
              if (mountedRef.current) {
                setConsentBlocked({
                  appName: typeof body.appName === 'string' ? body.appName : 'This PDApp',
                  required: Array.isArray(body.required) ? body.required as string[] : [],
                  reason: 'reentry',
                })
              }
            } else if (
              body
              && body.ok === false
              && (body.error === 'consent_required' || body.error === 'consent_declined')
              && typeof body.appFolderId === 'string'
            ) {
              const required = Array.isArray(body.required) ? body.required as string[] : []
              const appName = typeof body.appName === 'string' ? body.appName : 'This PDApp'
              // Captured while the guard above still narrows it — `body` is
              // reassigned below, which drops the narrowing.
              const gateFolderId: string = body.appFolderId
              // STILL MOUNTED? `runOnce()` is a full round trip, and the user can
              // close the file, switch to Source, or navigate during it. A dead
              // handler that reaches `takeFence` below TAKES the module-wide
              // fence and can never give it back: `openConsentDialog` calls
              // `setConsentRequest` on an unmounted fiber, so no dialog mounts,
              // nothing resolves the promise, and the `finally` that drops the
              // fence never runs — while the `[]`-deps cleanup that would have
              // released it already ran, before anything was held.
              //
              // Every later prompt for this app is then silently swallowed, in
              // EVERY viewer in the tab: the proactive gate bails on the fence,
              // the 412 path shows a block panel nobody earned, and its "Review
              // permission" is inert. Only a page reload recovers. The proactive
              // gate has had `if (cancelled || !req) return` at this exact seam
              // all along; these two sites had nothing.
              //
              // A BRANCH, not a bare `return`: the handler still owes the app a
              // response, and returning here would skip the `postMessage` below
              // and leave its promise pending. Answers exactly like the
              // fence-blocked case, minus the state write.
              if (!mountedRef.current) {
                body = { ok: false, error: 'consent_declined' }
              } else if (!screenActiveRef.current) {
                // #538 — a window in the BACKGROUND never asks: the person isn't looking at it. Its
                // app is told no for now; the proactive gate asks the moment the window is shown
                // (it re-runs on `screenActive`) and reloads the app once allowed.
                setConsentBlocked({ appName, required, reason: 'pending' })
                body = { ok: false, error: 'consent_declined' }
              } else if (consentGateOpen.has(gateFolderId)) {
                // A DIALOG IS OPEN for this app — this viewer's own, or another
                // mounted one's. Either way the question is still being asked,
                // so the panel must say so.
                //
                // CHECKED BEFORE `consentPromptedRef`, and that order is the
                // whole point. The proactive gate arms that ref BEFORE awaiting
                // its dialog, so while this viewer's own prompt is on screen its
                // app's boot call lands here — and under the old order it took
                // the "already asked ⇒ declined" branch and wrote a panel saying
                // the user had refused something they were at that moment being
                // asked about. The flag means "asked once this load", never
                // "declined"; the fence is what actually knows a dialog is up.
                //
                // Blocked rather than prompted: the other dialog is portaled and
                // visible, and the panel leaves a "Review" button for after.
                setConsentBlocked({ appName, required, reason: 'pending' })
                body = { ok: false, error: 'consent_declined' }
              } else if (consentPromptedRef.current) {
                // Asked once this load, no dialog open, and consent is still
                // unmet → the user really did decline (or dismissed). Don't
                // re-pop the dialog (no nag loop); surface the block panel
                // instead of letting the app run dead.
                setConsentBlocked({ appName, required, reason: 'declined' })
                body = { ok: false, error: 'consent_declined' }
              } else {
                consentPromptedRef.current = true
                // Through `takeFence`, so `fenceHeldRef`'s unmount cleanup
                // releases it. These sites had only the `finally` below, which
                // runs when the promise SETTLES — and an unmount never settles
                // it. In the SHARE view every gate comes through here (the
                // proactive one early-returns), so closing the file while a
                // prompt was open stranded the fence for the life of the page
                // and the app could never prompt again.
                // Non-null: the `else if` immediately above checked `has()`
                // synchronously, so nothing can have taken it in between.
                const gateToken = takeFence(gateFolderId)!
                let decision: Awaited<ReturnType<typeof openConsentDialog>>
                try {
                  const record = await consentRecordRef.current(body.appFolderId, required, Array.isArray(body.optional) ? body.optional as string[] : [])
                  // #1316: the session ended meanwhile (the record dropped with it) — no dialog for the next session
                  if (sessionEndedSince(start)) throw new SessionEndedError()
                  const consentParams = {
                    appFolderId: body.appFolderId,
                    appName,
                    appOwnerName: typeof body.appOwnerName === 'string' ? body.appOwnerName : null,
                    required,
                    optional: Array.isArray(body.optional) ? body.optional as string[] : [],
                    // The 412 body says what the manifest DECLARES, never what the
                    // user decided — see `consentRecord`. It returns `newFields`
                    // too, which is what keeps this a PROMPT (Decline / Allow)
                    // rather than a management view.
                    ...record,
                  }
                  // The HOST dialog, for the reason the proactive gate gives: the
                  // embed renders INSIDE its viewer's own subtree, and a
                  // backgrounded app deck is `opacity-0 pointer-events-none`, so
                  // a prompt relayed there can be mounted invisible and
                  // click-through. Portaled to `document.body`, it is visible
                  // whichever viewer owns the fence.
                  decision = await openConsentDialog(consentParams)
                } finally {
                  dropFence(gateFolderId, gateToken)
                }
                // #1316: decided after the session ended — never re-run as the next session, nothing written
                if (sessionEndedSince(start)) throw new SessionEndedError()
                // A real decision (allow OR decline) changed the grants → refresh the bar's mask.
                if (decision) refreshAccountsBarRef.current()
                if (!decision || decision.decision === 'decline') {
                  setConsentBlocked({ appName, required, reason: 'declined' })
                  body = { ok: false, error: 'consent_declined' }
                } else {
                  setConsentBlocked(null)
                  body = await runOnce()
                }
              }
            }
            // #184 Phase 4 — the platform's own fallback voice, raised BEFORE
            // the response goes back to the app.
            //
            // This is the choke point every run outcome passes through, and the
            // reason the toast needs no SDK transport change: the HOST performs
            // the fetch, so `body` here is the server's own answer rather than
            // anything the iframe composed. `authorNotice` is server-gated on
            // `readwrite` (see apps/api/src/v1/author-notice.ts) — there is
            // nothing to re-check here, and re-checking would mean deriving a
            // permission this component cannot see.
            //
            // Fires whether or not the app handles the failure. An app that
            // forgot the branch is a dead button and this is its only message;
            // an app that shows its own "Export failed." gets the WHY beside
            // it, which its five words could not have said.
            //
            // It does not need a `try` here, and that is a property of
            // `emitAppRunNotice` rather than of this line: it swallows a
            // throwing consumer itself. Load-bearing, because this call sits
            // inside the block whose `catch` answers the app `network_error` —
            // an escaping throw would skip the `postMessage` below and report a
            // COMPLETED `ok: true` run as a transport failure.
            //
            // `folderId` is in the key so two PDApps sharing a scaffold path
            // (`_scripts/export.py` in every one of them) do not silence each other.
            emitAppRunNotice(body, onAppRunNoticeRef.current, scriptPath, folderId)
            src.postMessage({ type: 'papan:run:response', requestId, ...body }, frameTargetOrigin())
          } catch (err) {
            // #1316: the answer landed after its session ended — the app never sees the old session's result
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:run:response', requestId, ok: false, error: 'session_ended' }, frameTargetOrigin()) } catch { /* gone */ } return }
            const msg = err instanceof Error ? err.message : String(err)
            try { src.postMessage({ type: 'papan:run:response', requestId, ok: false, error: 'network_error', stderr: msg }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        // papan.db.run → /api/v1/db/run. Same relay contract as papan:run: the
        // folder is the PARENT's mount context (parentIdRef), never iframe-supplied.
        // A PII op returns 412/403 → the SAME consent dialog + retry dance as
        // papan:run (consent is app-level, gated on the manifest's wants_identity,
        // so ONE grant serves both the .py and the .sql path).
        'papan:db:run': async (e) => {
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const op = typeof e.data.op === 'string' ? e.data.op : null
          if (!op) {
            try { src.postMessage({ type: 'papan:db:run:response', requestId, ok: false, error: 'invalid_args' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          const folderId = parentIdRef.current
          if (!folderId) {
            try { src.postMessage({ type: 'papan:db:run:response', requestId, ok: false, error: 'no_folder' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          // Forward the app's active subscription set (Tier 1): a WRITE returns
          // their fresh rows in `refreshed{}` so the app updates in one round-trip.
          const refresh = Array.isArray(e.data.refresh) ? (e.data.refresh as unknown[]).filter((x): x is string => typeof x === 'string') : []
          // #486 axis 2 — the `opts.database` selector (a name or the `_appdata/data.db` path);
          // the server validates it (main only, for now) and refuses a non-main selector.
          const database = typeof e.data.database === 'string' ? e.data.database : undefined
          const start = currentSessionSignal()   // #1316 — as papan:run above
          // #1517 — the app's `db.run(…, { timeoutMs })`: each REQUEST is aborted at that limit, body read
          // included, and the app is told `timeout` (the op may still have run). See `dbTimeoutMs`. Per
          // request: a consent dialog between the two requests below is a person deciding, never counted.
          const timeoutMs = dbTimeoutMs(e.data.timeoutMs)
          let timedOut = false
          const runOnce = async (): Promise<Record<string, unknown>> => {
            const limit = timeoutMs === undefined ? null : new AbortController()
            const timer = limit ? setTimeout(() => { timedOut = true; limit.abort() }, timeoutMs) : null   // before the body: the whole request
            let body: string
            try {
              body = JSON.stringify({ op, args: e.data.args, requestedFolderId: folderId, ...(refresh.length ? { refresh } : {}), ...(database ? { database } : {}) })
            } catch {
              if (timer) clearTimeout(timer)
              return dbArgsNotSendableBody()   // a BigInt / cycle arrives through postMessage but not as JSON: nothing sent (#1517)
            }
            try {
              const res = await sessionFetch(`${getApiBase()}/api/v1/db/run`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
                body,
                ...(limit ? { signal: limit.signal } : {}),
              })
              // #471 — same split as papan:run above; a 5xx on a WRITE op is ambiguous
              // about whether it applied, so it must not read as a clean failure.
              const read = await readInSession<Record<string, unknown>>(res, start, () => transportErrorBody(res.status))   // #1316 review round 1
              return timedOut ? dbTimedOutBody(timeoutMs!) : read
            } finally {
              if (timer) clearTimeout(timer)
            }
          }
          try {
            let body = await runOnce()
            // Identical 412/403 → consent → retry dance as papan:run above. The
            // 412 body carries the server-authoritative dialog fields; never read
            // display strings from any iframe message.
            if (body && body.ok === false && body.reentry_required === true) {
              // #796 — the app REQUIRES identity and this visit has none: a sign-in, not a
              // consent (the dialog could only fail — consent needs an account). The
              // panel's button hands off to the share viewer's own sign-in.
              if (mountedRef.current) {
                setConsentBlocked({
                  appName: typeof body.appName === 'string' ? body.appName : 'This PDApp',
                  required: Array.isArray(body.required) ? body.required as string[] : [],
                  reason: 'reentry',
                })
              }
            } else if (
              body
              && body.ok === false
              && (body.error === 'consent_required' || body.error === 'consent_declined')
              && typeof body.appFolderId === 'string'
            ) {
              const required = Array.isArray(body.required) ? body.required as string[] : []
              const appName = typeof body.appName === 'string' ? body.appName : 'This PDApp'
              // Captured while the guard above still narrows it — `body` is
              // reassigned below, which drops the narrowing.
              const gateFolderId: string = body.appFolderId
              // STILL MOUNTED? `runOnce()` is a full round trip, and the user can
              // close the file, switch to Source, or navigate during it. A dead
              // handler that reaches `takeFence` below TAKES the module-wide
              // fence and can never give it back: `openConsentDialog` calls
              // `setConsentRequest` on an unmounted fiber, so no dialog mounts,
              // nothing resolves the promise, and the `finally` that drops the
              // fence never runs — while the `[]`-deps cleanup that would have
              // released it already ran, before anything was held.
              //
              // Every later prompt for this app is then silently swallowed, in
              // EVERY viewer in the tab: the proactive gate bails on the fence,
              // the 412 path shows a block panel nobody earned, and its "Review
              // permission" is inert. Only a page reload recovers. The proactive
              // gate has had `if (cancelled || !req) return` at this exact seam
              // all along; these two sites had nothing.
              //
              // A BRANCH, not a bare `return`: the handler still owes the app a
              // response, and returning here would skip the `postMessage` below
              // and leave its promise pending. Answers exactly like the
              // fence-blocked case, minus the state write.
              if (!mountedRef.current) {
                body = { ok: false, error: 'consent_declined' }
              } else if (!screenActiveRef.current) {
                // #538 — a window in the BACKGROUND never asks: the person isn't looking at it. Its
                // app is told no for now; the proactive gate asks the moment the window is shown
                // (it re-runs on `screenActive`) and reloads the app once allowed.
                setConsentBlocked({ appName, required, reason: 'pending' })
                body = { ok: false, error: 'consent_declined' }
              } else if (consentGateOpen.has(gateFolderId)) {
                // Fence first — see the sibling handler above for why the order
                // matters (this viewer's own open dialog would otherwise be
                // reported as a decline the user never made).
                setConsentBlocked({ appName, required, reason: 'pending' })
                body = { ok: false, error: 'consent_declined' }
              } else if (consentPromptedRef.current) {
                setConsentBlocked({ appName, required, reason: 'declined' })
                body = { ok: false, error: 'consent_declined' }
              } else {
                consentPromptedRef.current = true
                // Through `takeFence`, so `fenceHeldRef`'s unmount cleanup
                // releases it. These sites had only the `finally` below, which
                // runs when the promise SETTLES — and an unmount never settles
                // it. In the SHARE view every gate comes through here (the
                // proactive one early-returns), so closing the file while a
                // prompt was open stranded the fence for the life of the page
                // and the app could never prompt again.
                // Non-null: the `else if` immediately above checked `has()`
                // synchronously, so nothing can have taken it in between.
                const gateToken = takeFence(gateFolderId)!
                let decision: Awaited<ReturnType<typeof openConsentDialog>>
                try {
                  const record = await consentRecordRef.current(body.appFolderId, required, Array.isArray(body.optional) ? body.optional as string[] : [])
                  // #1316: the session ended meanwhile (the record dropped with it) — no dialog for the next session
                  if (sessionEndedSince(start)) throw new SessionEndedError()
                  const consentParams = {
                    appFolderId: body.appFolderId,
                    appName,
                    appOwnerName: typeof body.appOwnerName === 'string' ? body.appOwnerName : null,
                    required,
                    optional: Array.isArray(body.optional) ? body.optional as string[] : [],
                    // The 412 body says what the manifest DECLARES, never what the
                    // user decided — see `consentRecord`. It returns `newFields`
                    // too, which is what keeps this a PROMPT (Decline / Allow)
                    // rather than a management view.
                    ...record,
                  }
                  // Host dialog + fence release — see the sibling handler above.
                  decision = await openConsentDialog(consentParams)
                } finally {
                  dropFence(gateFolderId, gateToken)
                }
                // #1316: decided after the session ended — never re-run as the next session, nothing written
                if (sessionEndedSince(start)) throw new SessionEndedError()
                if (decision) refreshAccountsBarRef.current()
                if (!decision || decision.decision === 'decline') {
                  setConsentBlocked({ appName, required, reason: 'declined' })
                  body = { ok: false, error: 'consent_declined' }
                } else {
                  setConsentBlocked(null)
                  body = await runOnce()
                }
              }
            }
            src.postMessage({ type: 'papan:db:run:response', requestId, ...body }, frameTargetOrigin())
          } catch (err) {
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:db:run:response', requestId, ok: false, error: 'session_ended' }, frameTargetOrigin()) } catch { /* gone */ } return } // #1316
            if (timedOut) { try { src.postMessage({ type: 'papan:db:run:response', requestId, ...dbTimedOutBody(timeoutMs!) }, frameTargetOrigin()) } catch { /* gone */ } return } // #1517
            // #1517 — a thrown request may already have been delivered: `response_lost`, never "never ran"
            try { src.postMessage({ type: 'papan:db:run:response', requestId, ...transportErrorBody(0) }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        // papan.db.batch(ops) → /api/v1/db/batch (B1 boot-read coalescing). Same relay
        // contract as papan:db:run: the folder is the PARENT's mount context (parentIdRef),
        // never iframe-supplied. No consent retry dance — batch is boot READS; an op that
        // needs identity returns `{ok:false, error:'consent_required'}` in its own slot and
        // the app grants via the bar / a first-class db.run, per the plan.
        'papan:db:batch': async (e) => {
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const ops = Array.isArray(e.data.ops) ? e.data.ops : null
          if (!ops) {
            try { src.postMessage({ type: 'papan:db:batch:response', requestId, ok: false, error: 'invalid_args' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          const folderId = parentIdRef.current
          if (!folderId) {
            try { src.postMessage({ type: 'papan:db:batch:response', requestId, ok: false, error: 'no_folder' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          const start = currentSessionSignal()   // #1316 review round 1: the body too is read in this session
          try {
            const res = await sessionFetch(`${getApiBase()}/api/v1/db/batch`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
              body: JSON.stringify({ ops, requestedFolderId: folderId }),
            })
            const body = await readInSession<Record<string, unknown>>(res, start, () => transportErrorBody(res.status)) // #471 — see papan:run
            // #796 — any op refused for a missing identity offers the share view's sign-in
            // (a batched boot read is how many apps first touch identity).
            offerSignInOnReentry(body, Array.isArray(body?.results) ? body.results : [])
            src.postMessage({ type: 'papan:db:batch:response', requestId, ...body }, frameTargetOrigin())
          } catch (err) {
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:db:batch:response', requestId, ok: false, error: 'session_ended' }, frameTargetOrigin()) } catch { /* gone */ } return } // #1316
            const msg = err instanceof Error ? err.message : String(err)
            try { src.postMessage({ type: 'papan:db:batch:response', requestId, ok: false, error: 'network_error', detail: msg }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        // papan.db.snapshot() → /api/v1/db/snapshot. Replies via the shared
        // papan:db:run:response type (requestId disambiguates). Folder is the
        // parent's mount context, never iframe-supplied.
        'papan:db:snapshot': async (e) => {
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const folderId = parentIdRef.current
          if (!folderId) {
            try { src.postMessage({ type: 'papan:db:run:response', requestId, ok: false, error: 'no_folder' }, frameTargetOrigin()) } catch { /* gone */ }
            return
          }
          const start = currentSessionSignal()   // #1316 review round 1: the body too is read in this session
          try {
            const res = await sessionFetch(`${getApiBase()}/api/v1/db/snapshot`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
              body: JSON.stringify({ requestedFolderId: folderId }),
            })
            const body = await readInSession<Record<string, unknown>>(res, start, () => transportErrorBody(res.status)) // #471 — see papan:run
            offerSignInOnReentry(body) // #796
            src.postMessage({ type: 'papan:db:run:response', requestId, ...body }, frameTargetOrigin())
          } catch (err) {
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:db:run:response', requestId, ok: false, error: 'session_ended' }, frameTargetOrigin()) } catch { /* gone */ } return } // #1316
            // #1517 — as papan:db:run: a thrown request may already have been delivered (a snapshot writes a version)
            try { src.postMessage({ type: 'papan:db:run:response', requestId, ...transportErrorBody(0) }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        // papan.db.bulkWrite / bulkWriteStatus relay handlers removed — the B2 lane is retired
        // (issue #604). The SDK no longer sends these frames.
        'papan:ws-token-request': async (e) => {
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          // RELAY CONTRACT (#185): the folder is the PARENT's mount context
          // (`parentIdRef`), NEVER the iframe-supplied `e.data.folderId`. The
          // server permission-gates it; the untrusted app can't name a folder.
          const folderId = parentIdRef.current ?? null
          const body: Record<string, unknown> = {}
          if (folderId) body.requestedFolderId = folderId
          const start = currentSessionSignal()   // #1316 review round 1: the body too is read in this session
          try {
            const res = await sessionFetch(`${getApiBase()}/api/v1/sdk/ws-token`, {
              method: 'POST',
              credentials: 'include',
              // Forward the active tenant/org-email so the API scopes the
              // ws-token to the org the app lives in (not the personal tenant).
              // Without this the WS canRead gate drops every file event.
              headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
              body: JSON.stringify(body),
            })
            const resBody = await readInSession<Record<string, unknown> | null>(res, start, () => null)
            if (res.status === 412) offerSignInOnReentry(resBody) // #796
            if (resBody && typeof resBody.token === 'string' && typeof resBody.exp === 'number') {
              // appFolderId forwarded out-of-band for the SDK's realtime cross-app
              // event filter (R8 — the encrypted token can't be decoded client-side).
              src.postMessage({ type: 'papan:ws-token:response', requestId, token: resBody.token, exp: resBody.exp, appFolderId: typeof resBody.appFolderId === 'string' ? resBody.appFolderId : null }, frameTargetOrigin())
            } else {
              src.postMessage({ type: 'papan:ws-token:response', requestId, token: null, exp: null }, frameTargetOrigin())
            }
          } catch (err) {
            // #1316: a token minted for the ended session is never handed to the app
            try { src.postMessage({ type: 'papan:ws-token:response', requestId, token: null, exp: null, ...(isSessionEndedError(err) ? { error: 'session_ended' } : {}) }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        'papan:whoami': async (e) => {
          // SDK papan.whoami() relays here so the call carries the PARENT's session
          // (the iframe holds no token). Folder comes from the parent's binding, not
          // the iframe, so it can't probe another app. Relay the raw status + body
          // back; the SDK runs the same parse as ppn-gg (Actor | null, or the loud
          // PapanWhoamiError on a consent gap).
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const folderId = parentIdRef.current ?? null
          const start = currentSessionSignal()   // #1316 review round 1: the body too is read in this session
          try {
            const res = await sessionFetch(`${getApiBase()}/api/v1/sdk/whoami`, {
              method: 'POST',
              credentials: 'include',
              // Forward the active tenant/org-email so whoami resolves the
              // owner's read in the org (else it 403s on a personal-scoped actor).
              headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
              body: JSON.stringify(folderId ? { appFolderId: folderId } : {}),
            })
            const body = await readInSession<Record<string, unknown> | null>(res, start, () => null)
            // #796 — no identity on an app that requires one: offer sign-in on the panel.
            if (res.status === 412) offerSignInOnReentry(body)
            src.postMessage({ type: 'papan:whoami:response', requestId, status: res.status, body }, frameTargetOrigin())
          } catch (err) {
            // #1316: never the identity of a session that ended while it was asked
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:whoami:response', requestId, status: 0, body: { error: 'session_ended' } }, frameTargetOrigin()) } catch { /* gone */ } return }
            const msg = err instanceof Error ? err.message : String(err)
            try { src.postMessage({ type: 'papan:whoami:response', requestId, status: 0, body: { error: 'whoami_failed', message: msg } }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        ...handoff.handlers,
        'papan:access': async (e) => {
          // SDK papan.access() relays here so the call carries the PARENT's session.
          // Folder from the parent binding (can't probe another app); path from the SDK.
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const folderId = parentIdRef.current ?? null
          const path = typeof e.data.path === 'string' ? e.data.path : null
          const start = currentSessionSignal()   // #1316 review round 1: the body too is read in this session
          try {
            const res = await sessionFetch(`${getApiBase()}/api/v1/sdk/access`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
              body: JSON.stringify({ ...(folderId ? { appFolderId: folderId } : {}), ...(path ? { path } : {}) }),
            })
            const body = await readInSession<Record<string, unknown> | null>(res, start, () => null)
            src.postMessage({ type: 'papan:access:response', requestId, status: res.status, body }, frameTargetOrigin())
          } catch (err) {
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:access:response', requestId, status: 0, body: { error: 'session_ended' } }, frameTargetOrigin()) } catch { /* gone */ } return } // #1316
            const msg = err instanceof Error ? err.message : String(err)
            try { src.postMessage({ type: 'papan:access:response', requestId, status: 0, body: { error: 'access_failed', message: msg } }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        'papan:pageLink': async (e) => {
          // `pdapp.links.page()` — the address of one of the app's OWN pages (a QR code).
          // It grants nothing, so nothing is asked and NOTHING IS MINTED: the app's existing
          // app link when this person may hand it out, else Papan's own address of the app.
          // The app is this viewer's binding (`appPage`), never anything the frame names.
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const start = currentSessionSignal()
          const answer = await answerPageLink(e.data, {
            app: appPageRef.current ?? null,
            fallbackTenantId: getActiveTenantId(),
            frontendOrigin: window.location.origin,
            linkOriginFor: async (folderId) => {
              const res = await sessionFetch(`${getApiBase()}/api/share-links/default-app-origin?fileId=${encodeURIComponent(folderId)}`, {
                credentials: 'include',
                headers: getDefaultHeaders(),
              })
              // A failed lookup THROWS (never a silent "no link"): answerPageLink catches it and falls back to
              // Papan's own address, on purpose.
              if (!res.ok) throw new Error(`default-app-origin answered ${res.status}`)
              const body = await readInSession<{ origin?: unknown } | null>(res, start, () => null)
              return typeof body?.origin === 'string' ? body.origin : null
            },
          })
          // #1316: an answer for a session that ended while it was asked is dropped.
          if (sessionEndedSince(start)) { try { src.postMessage({ type: 'papan:pageLink:response', requestId, error: 'unavailable', message: 'pdapp.links.page(): the session ended' }, frameTargetOrigin()) } catch { /* gone */ } return }
          try { src.postMessage({ type: 'papan:pageLink:response', requestId, ...answer }, frameTargetOrigin()) } catch { /* gone */ }
        },
        'papan:app-link': async (e) => {
          // #1617 — pdapp.qr.scan({ accept: 'this-app' }) asks whether a scanned URL is one of THIS
          // app's links. Relayed like papan:access: the parent's session, the folder from the
          // parent binding (an app can only ask about itself), the URL from the SDK.
          const src = e.source as Window | null
          if (!src) return
          const requestId = typeof e.data.requestId === 'string' ? e.data.requestId : null
          if (!requestId) return
          const folderId = parentIdRef.current ?? null
          const url = typeof e.data.url === 'string' ? e.data.url : ''
          const start = currentSessionSignal()   // #1316: the body too is read in this session
          try {
            const res = await sessionFetch(`${getApiBase()}/api/v1/sdk/app-link`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
              body: JSON.stringify({ ...(folderId ? { appFolderId: folderId } : {}), url }),
            })
            const body = await readInSession<Record<string, unknown> | null>(res, start, () => null)
            src.postMessage({ type: 'papan:app-link:response', requestId, status: res.status, body }, frameTargetOrigin())
          } catch (err) {
            if (isSessionEndedError(err)) { try { src.postMessage({ type: 'papan:app-link:response', requestId, status: 0, body: { error: 'session_ended' } }, frameTargetOrigin()) } catch { /* gone */ } return } // #1316
            const msg = err instanceof Error ? err.message : String(err)
            try { src.postMessage({ type: 'papan:app-link:response', requestId, status: 0, body: { error: 'app_link_failed', message: msg } }, frameTargetOrigin()) } catch { /* gone */ }
          }
        },
        'papan:vs': (e) => {
          if (viewSync && viewSyncId) {
            if (!viewSync[viewSyncId]) viewSync[viewSyncId] = {}
            viewSync[viewSyncId].scrollTop = e.data.st
            viewSync[viewSyncId].scrollLeft = e.data.sl
          }
        },
        'papan:nav': (e) => {
          // Pair this nav with the most-recent load on the SAME iframe so
          // the load-event probe (handleIframeLoad below) knows the iframe
          // is still on a preview-origin URL — our script is alive.
          if (e.source === iframe0Ref.current?.contentWindow) {
            lastNavGenRef.current.a = loadGenRef.current.a
          } else if (e.source === iframe1Ref.current?.contentWindow) {
            lastNavGenRef.current.b = loadGenRef.current.b
          }
          // NOTE: the patch-engine baseline is deliberately NOT posted from
          // here. This handler races the load-side posts (the child's nav
          // message can be processed AFTER triggerReload's onload already
          // posted the exact baseline), and a stale baseline poisons every
          // subsequent patch attempt into a reload loop. Baselines are
          // single-sourced from the two load sites, which both know the
          // EXACT content their load was served from.
          // Design mode: a nav = a fresh document (reload or in-app
          // navigation). Re-arm its inert inspector — posting to e.source
          // arms the INCOMING double-buffer before the swap. Selection
          // handling on reload (clear vs restore-by-path) is the EDITOR's
          // call — it happens in the onNavigate consumer, which fires next.
          if (designModeRef.current) {
            try {
              (e.source as Window | null)?.postMessage(
                { type: 'papan:design-mode', on: true, editableFile: designEditableFileRef.current ?? null },
                frameTargetOrigin(),
              )
            } catch { /* gone */ }
          }
          // Re-arm save-hotkey capture (and the agent eval bridge, same
          // editable-editor gate) on the INCOMING buffer before the swap
          // (same timing as the design re-arm above).
          if (onSaveHotkeyRef.current) {
            try {
              (e.source as Window | null)?.postMessage({ type: 'papan:enable-save-hotkey' }, frameTargetOrigin())
              ;(e.source as Window | null)?.postMessage({ type: 'papan:enable-eval' }, frameTargetOrigin())
            } catch { /* gone */ }
          }
          onNavigateRef.current?.(e.data.url)
        },
        'papan:design-select': (e) => {
          if (!designModeRef.current) return
          const sel = validateDesignSelect(e.data)
          if (sel) onDesignSelectRef.current?.(sel)
        },
        'papan:design-deselect': () => {
          if (!designModeRef.current) return
          onDesignSelectRef.current?.(null)
        },
        'papan:design-undo': (e) => {
          if (!designModeRef.current) return
          onDesignUndoRef.current?.(!!(e.data as { redo?: unknown }).redo)
        },
        'papan:design-edit-text': (e) => {
          if (!designModeRef.current) return
          const edit = validateDesignTextEdit(e.data)
          if (edit) onDesignEditTextRef.current?.(edit)
        },
        'papan:design-resize': (e) => {
          if (!designModeRef.current) return
          const commit = validateDesignResize(e.data)
          if (commit) onDesignResizeRef.current?.(commit)
        },
        'papan:design-reorder': (e) => {
          if (!designModeRef.current) return
          const reorder = validateDesignReorder(e.data)
          if (reorder) onDesignReorderRef.current?.(reorder)
        },
        'papan:design-icon-action': (e) => {
          if (!designModeRef.current) return
          const action = validateDesignIconAction(e.data)
          if (action) onDesignIconActionRef.current?.(action)
        },
        'papan:design-delete': (e) => {
          if (!designModeRef.current) return
          const del = validateDesignDelete(e.data)
          if (del) onDesignDeleteRef.current?.(del)
        },
        'papan:hotkey-save': () => {
          // Forwarded Cmd/Ctrl+S from the focused iframe (source-gated by the
          // dispatcher). Only wired when the embedder is an editable editor.
          onSaveHotkeyRef.current?.()
        },
        'papan:design-notice': (e) => {
          if (!designModeRef.current) return
          if (typeof e.data.code !== 'string' || !/^[a-z-]{1,40}$/.test(e.data.code)) return
          onDesignNoticeRef.current?.(e.data.code)
        },
        'papan:openFile': (e) => {
          // The typed SDK navigation request (`openFileBy*`). The wire `target` is
          // discriminated + RUNTIME-validated (it's from the untrusted app iframe).
          // FIRE-AND-FORGET: we never reply, so the app gets no exists/access oracle.
          const target = validateOpenFileTarget(e.data.target)
          if (!target) return // malformed / not a recognized target → drop silently
          const opts: OpenFileOptsWire = (e.data.opts && typeof e.data.opts === 'object') ? e.data.opts : {}
          // #11 LEADING-EDGE dedupe keyed by target: fire the FIRST open immediately (responsive,
          // and it rides the user's click gesture so window.open isn't popup-blocked), then SWALLOW
          // rapid repeats of the SAME target for ~150ms (a double-click / a re-render firing twice).
          // Distinct targets are independent — open A then B and BOTH fire. This is BY DESIGN, not
          // last-wins: a burst of distinct opens is a valid use (e.g. a dashboard opening several
          // files), and each lands in its own reusable surface (named window / workspace tab) so they
          // don't race a shared one. We only de-dupe accidental rapid repeats of the SAME target (a
          // double-click / re-render). An app that wants a burst to collapse to one open debounces
          // its OWN calls. (The superseded #7 trailing-edge "last-wins" debounce dropped opens the
          // app asked for AND lost the click gesture → window.open popup-blocked.) The server
          // rate-limit + popup-blocker remain the real spam backstops.
          const key = target.kind === 'fileId'
            ? `fileId:${target.fileId}@${target.fileTenantId}`
            : `${target.kind}:${target.path}`
          const fired = openFileFiredRef.current
          if (fired.has(key)) return // a repeat of this exact target within the window → swallow
          fired.set(key, setTimeout(() => fired.delete(key), 150))
          // RELAY CONTRACT: the app folder is the PARENT's mount context (`parentIdRef`),
          // never anything the iframe supplied — the resolver permission-gates from there.
          const appFolderId = parentIdRef.current ?? null
          if (onOpenFileTargetRef.current) {
            onOpenFileTargetRef.current(target, { appFolderId, opts })
          } else if (onOpenFileRef.current && target.kind === 'relativePath') {
            // Legacy fallback for consumers that only wired the path callback.
            onOpenFileRef.current(target.path)
          } else {
            warnOnce(
              'openFile',
              '[HtmlViewer] User HTML triggered papan:openFile but no `onOpenFileTarget` ' +
              'prop was wired. Dropping. Wire it to a resolve+open handler to enable in-app ' +
              'file opens from app content.',
            )
          }
        },
        'papan:openPdapp': (e) => {
          // `papan.openPdappByFolderId()` (#788) — LAUNCH another app. Same
          // contract as `papan:openFile`: validated because it comes from the
          // untrusted iframe, deduped so a double-click opens once, and NEVER
          // answered, so the app learns nothing about the target. The host
          // gates it on the VIEWER's own access and shows them any refusal.
          const target = validateOpenPdappTarget(e.data.target)
          if (!target) return // malformed / not a recognized target → drop silently
          const opts: OpenFileOptsWire = (e.data.opts && typeof e.data.opts === 'object') ? e.data.opts : {}
          // Shares the openFile dedupe map: the keys carry their own prefix, so
          // opening a file and launching an app never collide.
          const key = `pdapp:${target.folderId}@${target.tenantId}`
          const fired = openFileFiredRef.current
          if (fired.has(key)) return
          fired.set(key, setTimeout(() => fired.delete(key), 150))
          if (onOpenPdappTargetRef.current) {
            onOpenPdappTargetRef.current(target, { opts })
          } else {
            warnOnce(
              'openPdapp',
              '[HtmlViewer] User HTML triggered papan:openPdapp but no `onOpenPdappTarget` ' +
              'prop was wired. Dropping. Wire it to a resolve+launch handler to enable ' +
              'app-to-app links from app content.',
            )
          }
        },
        'papan:manage-permissions': () => {
          // The app called `papan.account.managePermissions()` — open the host's OWN
          // consent-management dialog (identical to the Papan bar's "Manage permissions"
          // path). USER-INITIATED and re-openable on every call: unlike the approve-once
          // automation gate (scripts.run/whoami 412), this is the "let me change what I
          // share" surface, so it must reopen each time the app asks. `openManageConsent`
          // guards on parentId + refuses while the SPA is framed (clickjack); the dialog
          // is host-rendered so the app can't style or auto-click it. On a real decision,
          // reload the app + refresh the bar (same as the bar-initiated path).
          void manageConsentRef.current()
        },
        'papan:openExternal': (e) => {
          // Phase 2: delegate to a parent-side confirm flow. The modal
          // mediates between legitimate `<a target="_blank">` clicks and
          // `window.open(attacker)` exfil attempts. If the consumer
          // didn't wire a confirm handler, drop the message — never fall
          // back to a direct window.open, which would let user HTML
          // trigger unconsented external navigations.
          if (typeof e.data.url !== 'string') return
          if (onRequestOpenExternalRef.current) {
            onRequestOpenExternalRef.current(e.data.url)
          } else {
            warnOnce(
              'openExternal',
              '[HtmlViewer] User HTML triggered papan:openExternal but ' +
              'no `onRequestOpenExternal` prop was wired. External links ' +
              'from the preview iframe are being dropped silently. Wire ' +
              'the prop to a confirm-then-open handler to enable external ' +
              'navigation.',
            )
          }
        },
        'papan:realtime-unavailable': (e) => {
          // The app's SDK reported a TERMINAL realtime failure (per-user WS cap,
          // 4008). Surface the host banner. We render our OWN copy and ignore the
          // iframe-supplied `reason` string entirely — never paint untrusted app
          // text as trusted chrome (R21 #140). Guard on a numeric code so a
          // malformed frame can't raise the banner.
          if (typeof e.data?.code === 'number') setRealtimeCapped(true)
        },
        'papan:needs-app-origin': (e) => {
          // Our own copy only; the frame picks between two fixed messages, nothing more.
          setNeedsAppOrigin(e.data?.kind === 'screen' ? 'screen' : 'media')
        },
      },
    })

    // Re-sync scroll on trigger
    useEffect(() => {
      if (!viewSyncTrigger || !viewSync || !viewSyncId) return
      const state = viewSync[viewSyncId]
      if (state?.scrollTop == null) return

      const msg = { type: 'papan:scrollTo', st: state.scrollTop, sl: state.scrollLeft ?? 0 }
      if (isSessionMode) {
        const active = activeIdx === 0 ? iframe0Ref.current : iframe1Ref.current
        active?.contentWindow?.postMessage(msg, frameTargetOrigin())
      } else {
        iframe0Ref.current?.contentWindow?.postMessage(msg, frameTargetOrigin())
      }
    }, [viewSyncTrigger, viewSync, viewSyncId, isSessionMode, activeIdx])

    // ─── Session mode: off-origin load probe ──────────────────────────
    // Bumps the per-iframe load gen on every load event and schedules a
    // check 500ms later. If a matching `papan:nav` (from our injected
    // script) updates lastNavGen, the iframe is on a preview-origin URL
    // — script alive, no off-origin signal. If not, the iframe has
    // navigated cross-origin (attacker.com or a legit external link our
    // injected script doesn't reach) and we surface `'about:external'`
    // for the chrome to render as "Now viewing: External site".
    //
    // Two guard rails:
    //   - Skip the `about:blank` initial mount fire on the background
    //     buffer (iframe1 sits idle until SSE swaps content in; its
    //     mount-time load event has no real URL and no script to send
    //     `papan:nav`, so without this skip the indicator falsely fires
    //     for every share-view render).
    //   - Bail on unmount via mountedRef so the timer doesn't poke a
    //     dead component or set state on a freshly-mounted next session.
    const handleIframeLoad = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = e.currentTarget
      if (!iframe.src || iframe.src === 'about:blank') return

      // Identify which buffer this load came from by ref identity, so the
      // gen pairing matches the same iframe's `papan:nav` message.
      const which: 'a' | 'b' = iframe === iframe1Ref.current ? 'b' : 'a'
      onFirstLoadRef.current?.()
      setBufferLoaded(prev => which === 'a'
        ? (prev[0] ? prev : [true, prev[1]])
        : (prev[1] ? prev : [prev[0], true]))
      loadGenRef.current[which]++
      const gen = loadGenRef.current[which]
      setTimeout(() => {
        if (!mountedRef.current) return
        if (lastNavGenRef.current[which] < gen) {
          onNavigateRef.current?.('about:external')
        }
      }, 500)
    }, [])

    // Mount lifecycle: flip mountedRef on unmount so any pending probe
    // setTimeouts bail before touching parent state.
    useEffect(() => {
      mountedRef.current = true
      const firedTimers = openFileFiredRef.current
      return () => {
        mountedRef.current = false
        // Clear the leading-edge openFile dedupe timers — parity with every other timer in
        // this component (the callbacks are no-ops post-unmount, but don't leave a special case).
        for (const t of firedTimers.values()) clearTimeout(t)
        firedTimers.clear()
      }
    }, [])

    // WebKit (iOS Safari) does NOT release a removed iframe's live resources —
    // notably its WebSocket connections — until the document is GC'd, which can
    // be much later. In session mode each preview iframe runs the app + its SDK
    // realtime WS; when this component unmounts on an SPA navigation (mobile:
    // workspace → drive → workspace), React just detaches the iframe and WebKit
    // keeps the old socket alive. Across a few workspace re-visits those zombie
    // sockets exhaust WebKit's small per-TAB WebSocket budget, and the NEXT
    // editor's collab socket can no longer open — the editor is wedged on
    // "Connecting…" (fresh page load works because it starts from a clean tab).
    // Fix: point each preview iframe at about:blank on UNMOUNT so WebKit unloads
    // the document and closes its sockets synchronously. Blink/Gecko already
    // release on removal, so this is a harmless no-op there.
    //
    // Two effects, deliberately: a holder refreshed after every commit tracks the
    // live iframes (robust to inline⇄session switches and to React detaching the
    // refs during unmount), and an UNMOUNT-ONLY effect ([] deps) blanks them. A
    // single no-deps effect would run its cleanup before every re-render and blank
    // the live preview mid-session — hence the split.
    // The accounts iframe's accessible name. Brand-derived rather than the
    // literal it was, because a screen reader on a reseller's deployment
    // announcing "Papan Accounts" names a product their user has never heard of.
    const { productName } = useBrand()

    const previewIframesRef = useRef<HTMLIFrameElement[]>([])
    useEffect(() => {
      previewIframesRef.current = [iframe0Ref.current, iframe1Ref.current].filter(Boolean) as HTMLIFrameElement[]
    })
    useEffect(() => {
      return () => {
        for (const f of previewIframesRef.current) {
          try { if (f.src && f.src !== 'about:blank') f.src = 'about:blank' } catch { /* detached */ }
        }
      }
    }, [])

    // ─── Inline mode: load ─────────────────────────────────────────────

    const handleInlineLoad = useCallback(() => {
      // Inline reload re-arms consent (the iframe re-ran its scripts).
      consentPromptedRef.current = false
      setConsentBlocked(null)
    }, [])

    // Papan-native block panel (outside the iframe so app CSS/JS can't fake
    // it). Shown when REQUIRED consent is declined — the app can't run, so we
    // say so instead of leaving a dead page. "Review permission" reloads →
    // re-arms the dialog; a manual refresh does the same.
    //
    // ── Stacking: the panel sits BELOW the accounts iframe ──────────────────
    //
    // Measured in the live page, after two attempts that reasoned from source and
    // were both wrong:
    //
    //   HTML Preview iframe    position:absolute  z-index 1
    //   Papan Accounts iframe  position:absolute  z-index 40
    //   this panel             position:absolute  z-index 50   ← painted over the bar
    //
    // All three are siblings in ONE stacking context, so 50 > 40 put the panel over
    // the accounts iframe — and that iframe is what RENDERS the consent dialog in
    // app mode. The `.pc-overlay { z-index: 9999 }` the dialog carries lives inside
    // that iframe's own document; it cannot lift the frame above a sibling of its
    // host. Reading that 9999 and concluding "the dialog wins" was the mistake.
    //
    // So the panel must cover the app (z 0/1) and nothing above it. 30 does that.
    //
    // It deliberately stays MOUNTED while a dialog is open: dismiss the prompt a
    // second time and the way back has to still be there. An earlier cut hid it on
    // `consentRequest`, which is inert for this path anyway (the dialog belongs to
    // the iframe, not to SPA state) and, where it did fire, removed the very
    // affordance that was asked for.
    const blockPanel = consentBlocked && (
      // #538 — the glass material of the app's other dialogs (DESIGN_DOC §10.5), in the SPA's theme.
      // zIndex inline: below the accounts frame, above the app it covers (consent-retry-rearm pins both).
      <div className="absolute inset-0 flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]" style={{ zIndex: 30 }}>
        <div role="alertdialog" aria-labelledby="pdapp-block-title" className={`${blockThemeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-[400px] px-6 pt-6 pb-5 text-center`}>
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full themed-bg-tertiary themed-text-secondary" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {consentBlocked.reason === 'reentry'
                ? <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="m10 17 5-5-5-5" /><path d="M15 12H3" /></>
                : <><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="M12 8v4" /><path d="M12 16h.01" /></>}
            </svg>
          </div>
          <h3 id="pdapp-block-title" className="text-[15px] font-semibold themed-text">
            {consentBlocked.reason === 'reentry'
              ? `${consentBlocked.appName} needs you to sign in`
              : `${consentBlocked.appName} needs your permission to run`}
          </h3>
          <p className="mt-2 text-[13px] leading-relaxed themed-text-secondary">
            {/* Say only what is true of THIS reason. `pending` means another
                open viewer of the same app holds the consent fence and is
                asking right now (or this window was in the background) — the
                user has not declined anything, and telling them they had was a
                confident false statement about their own decision on the one
                surface that explains why the app is dead. */}
            It requires <b className="font-semibold themed-text">{consentBlocked.required.map(labelFor).join(', ')}</b>
            {consentBlocked.reason === 'pending'
              ? '. Answer the permission dialog to continue — it may be open in another window.'
              : consentBlocked.reason === 'reentry'
                ? ', and you are not signed in on this link. Sign in and choose who to use it as.'
                : ' and you declined. Nothing here runs until you allow it.'}
          </p>
          <div className="mt-5">
            {consentBlocked.reason === 'reentry' ? (
              // The share viewer's chrome owns sign-in (SHARE_SIGN_IN_EVENT): the quick-view
              // Sign in → identity picker, the one flow every share visitor uses.
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent(SHARE_SIGN_IN_EVENT))}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 cursor-pointer"
              >
                Sign in
              </button>
            ) : isSessionMode ? (
              <button
                type="button"
                onClick={retryConsent}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 cursor-pointer"
              >
                Review permission
              </button>
            ) : (
              <p className="text-xs themed-text-muted">Refresh the preview to review.</p>
            )}
          </div>
        </div>
      </div>
    )

    // Realtime-cap banner — LOUD but non-blocking (the app still renders; only
    // live updates are gone). Self-contained inline styles so it's theme-agnostic
    // like blockPanel. Host-authored copy only. Dismissible; reload restores it.
    const realtimeBanner = realtimeCapped && (
      <div
        role="alert"
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 45,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px',
          background: '#7c2d12', color: '#ffedd5',
          borderBottom: '1px solid #9a3412',
          fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
          fontSize: 13, lineHeight: 1.4,
          boxShadow: '0 1px 6px rgba(0,0,0,.35)',
        }}
      >
        <span aria-hidden style={{ fontSize: 15 }}>⚡</span>
        <span style={{ flex: 1 }}>
          <b style={{ fontWeight: 600 }}>Live updates paused.</b>{' '}
          Too many {productName} apps are open at once. Close some and reload this app to restore realtime.
        </span>
        <button
          onClick={() => setRealtimeCapped(false)}
          aria-label="Dismiss"
          style={{
            flexShrink: 0, border: 'none', background: 'transparent',
            color: '#fed7aa', cursor: 'pointer', fontSize: 16, lineHeight: 1,
            padding: '2px 6px', borderRadius: 4,
          }}
        >
          ×
        </button>
      </div>
    )

    // Camera hint (#1408 stage 1) — same shape as the realtime banner, below it when
    // both show. The button is offered only in a person's own drive (`app`): a share
    // visitor acts as the link, and the app's own link is not theirs to hand out.
    const canOpenOnOwnLink = accountsContext === 'app' && !!parentId
    const openOnOwnLink = async () => {
      if (!parentId) return
      setAppLinkError(null)
      // Opened now, while the click still counts, and pointed at the link once it is known.
      const w = window.open('', '_blank')
      try {
        const res = await ensureDefaultAppLink(parentId)
        if (!res.ok) {
          w?.close()
          setAppLinkError(res.structural
            ? "This app's folder can't have a link of its own."
            : res.error === 'no_share_capability'
              ? "You can't share this app, so it can't be opened on its own link."
              : "Couldn't get this app's link.")
          return
        }
        if (!w) { setAppLinkError('Your browser blocked the new tab. Allow pop-ups for this site and try again.'); return }
        w.opener = null
        w.location.href = res.link.url
      } catch {
        w?.close()
        setAppLinkError("Couldn't get this app's link.")
      }
    }
    const appOriginBanner = needsAppOrigin && (
      <div
        role="alert"
        data-testid="needs-app-origin-banner"
        style={{
          position: 'absolute', top: realtimeCapped ? 40 : 0, left: 0, right: 0, zIndex: 45,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px',
          background: '#1e3a5f', color: '#e0f2fe',
          borderBottom: '1px solid #1e40af',
          fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
          fontSize: 13, lineHeight: 1.4,
          boxShadow: '0 1px 6px rgba(0,0,0,.35)',
        }}
      >
        <span aria-hidden style={{ fontSize: 15 }}>🎥</span>
        <span style={{ flex: 1 }}>
          <b style={{ fontWeight: 600 }}>
            {needsAppOrigin === 'screen' ? "Screen sharing doesn't work in this preview." : "The camera and microphone don't work in this preview."}
          </b>{' '}
          {appLinkError ?? (canOpenOnOwnLink
            ? 'Open the app on its own link to use them.'
            : 'Open the app from its own link to use them.')}
        </span>
        {canOpenOnOwnLink && (
          <button
            onClick={() => { void openOnOwnLink() }}
            data-testid="needs-app-origin-open"
            style={{
              flexShrink: 0, border: '1px solid #93c5fd', background: 'transparent',
              color: '#e0f2fe', cursor: 'pointer', fontSize: 12, lineHeight: 1.2,
              padding: '4px 10px', borderRadius: 6,
            }}
          >
            Open on its own link
          </button>
        )}
        <button
          onClick={() => setNeedsAppOrigin(null)}
          aria-label="Dismiss"
          style={{
            flexShrink: 0, border: 'none', background: 'transparent',
            color: '#bae6fd', cursor: 'pointer', fontSize: 16, lineHeight: 1,
            padding: '2px 6px', borderRadius: 4,
          }}
        >
          ×
        </button>
      </div>
    )

    // Papan Accounts embed — same-origin sibling of the app iframe (the app
    // can't reach it). Thin bottom bar; full overlay when it asks to expand.
    //
    // `email=` disambiguates WHICH membership in a multi-email acting org — the
    // embed sends it as x-org-email on its own first-party /sdk/consent* fetches.
    // Without it those fetches 401 "org_email_required" for a 2+-membership org
    // (the requirement comes back empty → the "Manage permissions" surface is
    // hidden even though the identity bar renders). Share-view carries the bound
    // recipient's `shareActingEmail`; the drive/workspace view reads the SPA's
    // live active org-email (same value getDefaultHeaders sends on the HOST's own
    // fetches, which is why THOSE resolve). Null for a personal tenant — omitted,
    // and the embed falls back to the primary, which is correct there.
    // #794 — the email a Personal identity CHOSE for this share (the session's verified email):
    // the bar DISPLAYS it as "you" (what the app is told after consent). Display only — `email=`
    // above stays the membership key the bar's own fetches send.
    const embedChosenEmail = accountsContext === 'share'
      ? (shareVerifiedEmail !== undefined ? shareVerifiedEmail : (shareSessionContext(shareSessionToken)?.verifiedEmail ?? null))
      : null
    const embedActingEmail = accountsContext === 'share'
      ? (shareActingEmail ?? null)
      : getActiveOrgEmail()
    // Share-view consent must be written FIRST-PARTY (/accounts/ppn-consent) — the
    // share-session principal the share-view sends is rejected by /sdk/consent
    // (consent_must_be_first_party). Carries the acting org identity + share token.
    // MEMOISED. `SdkConsentDialog`'s effect lists this in its deps, and a fresh
    // object literal every render tore the dialog down and rebuilt it from the
    // seeded defaults — discarding whatever the user had toggled. Harmless while
    // only Manage used this dialog in share context; now every share prompt does,
    // and a re-render is one `setConsentBlocked` away (the app's second boot call
    // hits the 412 handler and sets it). The user switches a field off, the
    // dialog remounts with it back ON, and Allow grants what they just refused.
    const shareConsentRouting = useMemo(
      () => (accountsContext === 'share'
        ? { tenantId: shareActingTenantId ?? null, email: shareActingEmail ?? null, shareToken: shareToken ?? null, shareSession: shareSessionToken ?? null }
        : null),
      [accountsContext, shareActingTenantId, shareActingEmail, shareToken, shareSessionToken],
    )
    // #538 (trial): in the drive / workspace the bar is a capsule FLOATING at the bottom centre over
    // the app — its frame sized to the capsule (the bar reports it), so it covers nothing else; hidden,
    // it is a small round button there. The share view keeps its thin bottom strip.
    const barOnTop = accountsContext === 'app'
    const embedFrame = embedHidden ? null : (
      <iframe
        // Hard-remount the bar when the folder OR acting identity changes, so it can't
        // carry a prior folder's grant across a navigation/identity switch (the
        // "unmasked email on the duplicated folder" staleness — the bar showed the
        // source folder's consented email until a manual reload). A key change forces a
        // fresh mount → load() re-reads consent for the NEW (folder, identity).
        key={`acct:${accountsContext}:${barActions?.isPinned ? 'pin' : ''}:${parentId ?? ''}:${shareActingTenantId ?? ''}:${embedActingEmail ?? ''}:${accountsContext === 'share' ? (shareSessionToken ?? '') : ''}`}
        ref={embedRef}
        {...{ [PAPAN_FRAME_ATTR]: '' }}
        title={`${productName} Accounts`}
        src={`${typeof window !== 'undefined' ? window.location.origin : ''}/__accounts/embed.html?${parentId ? `folderId=${encodeURIComponent(parentId)}&` : ''}${accountsContext === 'share' && shareId ? `shareId=${encodeURIComponent(shareId)}&` : ''}${accountsContext === 'share' && shareActingTenantId ? `tenant=${encodeURIComponent(shareActingTenantId)}&` : ''}${embedActingEmail ? `email=${encodeURIComponent(embedActingEmail)}&` : ''}${embedChosenEmail ? `ve=${encodeURIComponent(embedChosenEmail)}&` : ''}${accountsContext === 'share' && shareSessionToken ? `sid=${encodeURIComponent(shareSessionToken)}&` : ''}${barOnTop ? `acts=${[barActions?.onShare ? 'share' : '', barActions?.onTogglePin ? 'fav' : ''].filter(Boolean).join(',')}&${barActions?.isPinned ? 'pinned=1&' : ''}` : ''}context=${accountsContext}`}
        className="border-0"
        style={embedExpanded
          ? { position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 40 }
          : barOnTop
            ? { position: 'absolute', left: '50%', bottom: 0, transform: 'translateX(-50%)', width: barBox.w, maxWidth: '100%', height: barBox.h, zIndex: 40, background: 'transparent' }
            : { position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', height: 28, zIndex: 40 }}
      />
    )

    // ─── Session mode: render two iframes with double-buffering ────────

    if (isSessionMode) {
      return (
        <div className="relative w-full h-full">
          <iframe
            ref={iframe0Ref}
            sandbox={frameAppOrigin ? APP_ORIGIN_FRAME_SANDBOX : OPAQUE_FRAME_SANDBOX}
            {...(frameAppOrigin ? { allow: APP_ORIGIN_FRAME_ALLOW } : {})}
            // The page's own URL never reaches the content: a same-origin preview would
            // otherwise hand it the parent's URL (a share view's session) in document.referrer.
            referrerPolicy="no-referrer"
            title="HTML Preview A"
            className="absolute inset-0 w-full h-full border-0"
            style={{
              backgroundColor: bufferLoaded[0] ? PREVIEW_BACKDROP : undefined,
              visibility: activeIdx === 0 ? 'visible' : 'hidden',
              zIndex: activeIdx === 0 ? 1 : 0,
              // An app fights Papan's picker for focus: hidden until it closes.
              ...(handoff.framesHidden ? { display: 'none' } : {}),
            }}
            onLoad={handleIframeLoad}
          />
          <iframe
            ref={iframe1Ref}
            sandbox={frameAppOrigin ? APP_ORIGIN_FRAME_SANDBOX : OPAQUE_FRAME_SANDBOX}
            {...(frameAppOrigin ? { allow: APP_ORIGIN_FRAME_ALLOW } : {})}
            // The page's own URL never reaches the content: a same-origin preview would
            // otherwise hand it the parent's URL (a share view's session) in document.referrer.
            referrerPolicy="no-referrer"
            title="HTML Preview B"
            className="absolute inset-0 w-full h-full border-0"
            style={{
              backgroundColor: bufferLoaded[1] ? PREVIEW_BACKDROP : undefined,
              visibility: activeIdx === 1 ? 'visible' : 'hidden',
              zIndex: activeIdx === 1 ? 1 : 0,
              ...(handoff.framesHidden ? { display: 'none' } : {}),
            }}
            onLoad={handleIframeLoad}
          />
          {consentRequest && (
            <SdkConsentDialog
              request={consentRequest}
              onClose={() => setConsentRequest(null)}
              shareRouting={shareConsentRouting}
            />
          )}
          {dragCatcher}
          {handoff.ui}
          {blockPanel}
          {realtimeBanner}
          {appOriginBanner}
          {barOnTop
            ? (!embedHidden && parentId ? (
              <PdappCapsule
                requirementUrl={consentRequirementUrl(parentId)}
                refreshKey={capsuleRefresh}
                onPermissions={() => void manageConsent()}
                onShare={barActions?.onShare}
                onLocate={barActions?.onLocate}
                onOpenList={barActions?.onOpenList}
                isPdapp={barActions?.isPdapp}
                active={screenActive}
                favoriteAppId={barActions?.favoriteAppId}
                icon={barActions?.icon}
              />
            ) : null)
            : embedFrame}
        </div>
      )
    }

    // ─── Inline mode: single iframe with srcDoc ────────────────────────

    return (
      <div className="relative w-full h-full">
        {/* (D7) Distinct `key` from the session-mode iframes (HTML Preview A/B).
            Session mode sets the iframe `src` IMPERATIVELY via the ref (double
            buffer); without a distinct key React reuses that same DOM element here
            and applies `srcDoc` as a prop without clearing the stale `src` — and
            the browser prefers `src` over `srcDoc`, so inline mode would keep
            showing the live session. A fresh key forces a clean remount (srcDoc
            only). The cost is a brief reload on the mode switch, fine for a snapshot. */}
        <iframe
          key="html-preview-inline"
          ref={iframe0Ref}
          sandbox="allow-scripts allow-popups allow-forms"
          referrerPolicy="no-referrer"
          {...(src ? { src } : { srcDoc })}
          title="HTML Preview"
          className="w-full h-full border-0"
          style={{ backgroundColor: PREVIEW_BACKDROP }}
          onLoad={handleInlineLoad}
        />
        {consentRequest && (
          <SdkConsentDialog
            request={consentRequest}
            onClose={() => setConsentRequest(null)}
            shareRouting={shareConsentRouting}
          />
        )}
        {blockPanel}
        {realtimeBanner}
        {appOriginBanner}
      </div>
    )
  }
)
